import React, { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';

// --- Context for managing application state ---
const CoilContext = createContext();

// Constants for the permanent Sales Godown
const SALES_GODOWN_NAME = "Sales";
const SALES_GODOWN_ID = "sales-godown-fixed-id";

const CoilProvider = ({ children }) => {
  // State variables for Firebase and application data
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [firebaseUid, setFirebaseUid] = useState(null); // Actual UID from Firebase auth (can be anonymous)
  // Initialize appDataUserId and isAdminLoggedIn from localStorage for persistence
  const [appDataUserId, setAppDataUserId] = useState(localStorage.getItem('appDataUserId') || null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(localStorage.getItem('isAdminLoggedIn') === 'true');

  const [rawCoils, setRawCoils] = useState([]);
  const [processedItems, setProcessedItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [processingReports, setProcessingReports] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ isOpen: false, message: '', onConfirm: null, type: 'info' });
  const [notification, setNotification] = useState({ isOpen: false, message: '', type: 'info' });

  // Safely define appId, providing fallbacks
  // eslint-disable-next-line no-undef
  const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';

  // Hardcoded Firebase Configuration
  const firebaseConfig = {
    apiKey: "AIzaSyBqVb7qMO78bzUSCDcXQqApH9Cd16d6AFo",
    authDomain: "kra1-a1b3c.firebaseapp.com",
    projectId: "kra1-a1b3c",
    storageBucket: "kra1-a1b3c.firebasestorage.app",
    messagingSenderId: "450604595923",
    appId: "1:450604595923:web:9522cbe8a9eb491c03f226",
    measurementId: "G-203DXMWMY6"
  };

  // --- Notification Management Functions ---
  const showNotification = useCallback((message, type = 'info', duration = 3000) => {
    setNotification({ isOpen: true, message: String(message), type });
    setTimeout(() => {
      setNotification({ isOpen: false, message: '', type: 'info' });
    }, duration);
  }, []);

  // --- Modal Management Functions ---
  const showModal = useCallback((message, type = 'info', onConfirm = null) => {
    setModal({ isOpen: true, message: String(message), onConfirm, type });
  }, []);

  const hideModal = useCallback(() => {
    setModal({ isOpen: false, message: '', onConfirm: null, type: 'info' });
  }, []);

  // --- Firebase Initialization and Authentication ---
  useEffect(() => {
    console.log("CoilProvider: Firebase Init Effect Starting...");
    if (Object.keys(firebaseConfig).length === 0) {
      console.error("CoilProvider: Firebase config is empty. Cannot initialize Firebase.");
      setLoading(false);
      showModal("Firebase configuration is unexpectedly empty. Cannot initialize Firebase.", "error");
      return;
    }

    try {
      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const firebaseAuth = getAuth(app);

      setDb(firestoreDb);
      setAuth(firebaseAuth);

      const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
        if (user) {
          setFirebaseUid(user.uid);
          console.log("CoilProvider: Firebase User authenticated:", user.uid);
        } else {
          console.log("CoilProvider: No Firebase user, signing in anonymously...");
          await signInAnonymously(firebaseAuth);
        }
        setLoading(false);
      });

      return () => {
        console.log("CoilProvider: Cleaning up auth listener.");
        unsubscribe();
      };
    } catch (error) {
      console.error("CoilProvider: Failed to initialize Firebase:", error);
      setLoading(false);
      showModal("Error initializing Firebase. Please check console for details.", "error");
    }
  }, [appId, firebaseConfig, showModal]);

  // --- Firestore Data Listeners ---
  useEffect(() => {
    let unsubscribeRawCoils = () => {};
    let unsubscribeProcessedItems = () => {};
    let unsubscribeGodowns = () => {};
    let unsubscribeProcessingReports = () => {};
    let unsubscribeAppSettings = () => {};

    if (db && appDataUserId) {
      console.log("CoilProvider: Setting up Firestore data listeners for appDataUserId:", appDataUserId);

      const pathPrefix = `/artifacts/${appId}/users/${appDataUserId}`;
      const publicPathPrefix = `/artifacts/${appId}/public`;

      unsubscribeRawCoils = onSnapshot(collection(db, `${pathPrefix}/rawCoils`), (snapshot) => {
        setRawCoils(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("CoilProvider: Error fetching raw coils:", error);
        showModal("Error fetching raw coils data.", "error");
      });

      unsubscribeProcessedItems = onSnapshot(collection(db, `${pathPrefix}/processedItems`), (snapshot) => {
        setProcessedItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("CoilProvider: Error fetching processed items:", error);
        showModal("Error fetching processed items data.", "error");
      });

      unsubscribeGodowns = onSnapshot(collection(db, `${pathPrefix}/godowns`), (snapshot) => {
        setGodowns(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("CoilProvider: Error fetching godowns:", error);
        showModal("Error fetching godowns data.", "error");
      });

      unsubscribeProcessingReports = onSnapshot(collection(db, `${pathPrefix}/processingReports`), (snapshot) => {
        setProcessingReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("CoilProvider: Error fetching processing reports:", error);
        showModal("Error fetching processing reports data.", "error");
      });

      unsubscribeAppSettings = onSnapshot(doc(db, `${publicPathPrefix}/data/appSettings/general`), (docSnap) => {
        setAppSettings(docSnap.exists() ? docSnap.data() : {});
      }, (error) => {
        console.error("CoilProvider: Error fetching app settings:", error);
        showModal("Error fetching app settings.", "error");
      });
    } else {
      console.log("CoilProvider: DB or AppDataUserID not ready. Clearing data states.");
      setRawCoils([]);
      setProcessedItems([]);
      setGodowns([]);
      setProcessingReports([]);
      setAppSettings({});
    }

    return () => {
      console.log("CoilProvider: Cleaning up Firestore data listeners.");
      unsubscribeRawCoils();
      unsubscribeProcessedItems();
      unsubscribeGodowns();
      unsubscribeProcessingReports();
      unsubscribeAppSettings();
    };
  }, [db, appDataUserId, appId, showModal]);

  // --- Helper to wrap async Firestore actions ---
  const wrapFirestoreAction = useCallback((actionFn, successMsg, errorBaseMsg) => async (...args) => {
    if (!db || !appDataUserId) {
      showModal("System not ready or not logged in. Please wait.", "error");
      return false;
    }
    setLoading(true);
    try {
      const result = await actionFn(...args);
      if (successMsg) showNotification(successMsg, "success");
      return result !== undefined ? result : true; // Return true on success if actionFn doesn't return
    } catch (e) {
      console.error(`Error: ${errorBaseMsg}`, e);
      showModal(`${errorBaseMsg}: ${e.message}`, "error");
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  }, [db, appDataUserId, showModal, showNotification]);

  // --- Helper to wrap async Firestore actions with confirmation ---
  const wrapFirestoreConfirmAction = useCallback((actionFn, confirmMsg, successMsg, errorBaseMsg) => (...args) => {
    if (!db || !appDataUserId) {
      showModal("System not ready or not logged in. Please wait.", "error");
      return;
    }
    showModal(confirmMsg, "confirm", async () => {
      setLoading(true);
      try {
        const result = await actionFn(...args);
        if (successMsg) showNotification(successMsg, "success");
        return result !== undefined ? result : true;
      } catch (e) {
        console.error(`Error: ${errorBaseMsg}`, e);
        showModal(`${errorBaseMsg}: ${e.message}`, "error");
        return false;
      } finally {
        setLoading(false);
      }
    });
  }, [db, appDataUserId, showModal, showNotification]);

  // Function to handle admin login/logout
  const handleAdminLogin = useCallback(async (fixedUserId) => {
    setAppDataUserId(fixedUserId);
    setIsAdminLoggedIn(true);
    localStorage.setItem('appDataUserId', fixedUserId);
    localStorage.setItem('isAdminLoggedIn', 'true');

    // Ensure Sales godown exists on admin login
    if (db) {
        const salesGodownRef = doc(db, `/artifacts/${appId}/users/${fixedUserId}/godowns`, SALES_GODOWN_ID);
        try {
            const docSnap = await getDoc(salesGodownRef);
            if (!docSnap.exists()) {
                console.log("Creating Sales godown...");
                await setDoc(salesGodownRef, {
                    id: SALES_GODOWN_ID,
                    name: SALES_GODOWN_NAME,
                    location: "Sales Department",
                    isSystemGodown: true // Mark as system-managed
                });
                console.log("Sales godown created.");
            }
        } catch (error) {
            console.error("Error ensuring Sales godown exists:", error);
            showModal("Failed to ensure Sales godown exists. Please try again.", "error");
        }
    }
  }, [db, appId, showModal]);

  const handleAdminLogout = useCallback(() => {
    setAppDataUserId(null);
    setIsAdminLoggedIn(false);
    localStorage.removeItem('appDataUserId');
    localStorage.removeItem('isAdminLoggedIn');
    setRawCoils([]);
    setProcessedItems([]);
    setGodowns([]);
    setProcessingReports([]);
    setAppSettings({});
  }, []);

  // --- CRUD Operations using the new helpers ---
  const addRawCoil = wrapFirestoreAction(async (coilData) => {
    const newCoil = {
      ...coilData,
      status: 'available',
      timestamp: new Date().toISOString(),
      id: `RC-${Date.now()}`,
      initialWeight: coilData.weight
    };
    await setDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, newCoil.id), newCoil);
  }, "Raw coil added successfully!", "Error adding raw coil");

  const updateRawCoil = wrapFirestoreAction(async (id, updatedData) => {
    await updateDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, id), updatedData);
  }, "Raw coil updated successfully!", "Error updating raw coil");

  const deleteRawCoil = wrapFirestoreConfirmAction(async (id) => {
    const processedQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`), where("parentId", "==", id));
    const processedSnapshot = await getDocs(processedQuery);
    if (!processedSnapshot.empty) {
      throw new Error("It has associated processed items. Please delete processed items first.");
    }
    await deleteDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, id));
  }, "Are you sure you want to delete this raw coil? This action cannot be undone.", "Raw coil deleted successfully!", "Error deleting raw coil");

  const addProcessedItem = wrapFirestoreAction(async (itemData) => {
    const newItem = {
      ...itemData,
      id: `${itemData.type === 'sheet' ? 'PS' : 'SC'}-${Date.now()}`
    };
    await setDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`, newItem.id), newItem);
  }, "Processed item added successfully!", "Error adding processed item");

  const updateProcessedItem = wrapFirestoreAction(async (id, updatedData) => {
    const itemRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`, id);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) {
      throw new Error("Processed item not found.");
    }
    const oldData = itemSnap.data();

    const parentCoilRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, oldData.parentId);
    const parentCoilSnap = await getDoc(parentCoilRef);
    if (!parentCoilSnap.exists()) {
      throw new Error("Original raw coil for this item not found.");
    }
    const parentCoil = parentCoilSnap.data();

    const weightDifference = updatedData.weight - oldData.weight;
    const newParentCoilWeight = parentCoil.weight - weightDifference;
    let newParentCoilStatus = parentCoil.status;

    if (Math.abs(newParentCoilWeight - parentCoil.initialWeight) < 0.01) {
      newParentCoilStatus = 'available';
    } else if (newParentCoilWeight < parentCoil.initialWeight && newParentCoilStatus !== 'processed-out') {
      newParentCoilStatus = 'partially-processed';
    } else if (newParentCoilWeight <= 0.01 && newParentCoilStatus !== 'processed-out') {
      newParentCoilStatus = 'processed-out';
    }

    await updateDoc(itemRef, updatedData);
    await updateDoc(parentCoilRef, {
      weight: newParentCoilWeight,
      status: newParentCoilStatus
    });
  }, "Processed item updated and parent coil adjusted successfully!", "Error updating processed item");

  const deleteProcessedItem = wrapFirestoreConfirmAction(async (id) => {
    const itemRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`, id);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) {
      throw new Error("Processed item not found.");
    }
    const itemToDelete = itemSnap.data();

    const parentCoilRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, itemToDelete.parentId);
    const parentCoilSnap = await getDoc(parentCoilRef);

    if (parentCoilSnap.exists()) {
      const parentCoil = parentCoilSnap.data();
      const restoredWeight = parentCoil.weight + itemToDelete.weight;
      let newStatus = parentCoil.status;

      if (Math.abs(restoredWeight - parentCoil.initialWeight) < 0.01) {
        newStatus = 'available';
      } else if (restoredWeight < parentCoil.initialWeight) {
        newStatus = 'partially-processed';
      } else if (parentCoil.status === 'processed-out') {
        newStatus = 'partially-processed';
      }

      await updateDoc(parentCoilRef, { weight: restoredWeight, status: newStatus });
    } else {
      console.warn("Parent raw coil not found for processed item:", itemToDelete.parentId);
    }
    await deleteDoc(itemRef);
  }, "Are you sure you want to delete this processed item? This action cannot be undone and will restore weight to the raw coil.", "Processed item deleted and raw coil weight restored successfully!", "Error deleting processed item");

  const addGodown = wrapFirestoreAction(async (godownData) => {
    const newGodown = { ...godownData, id: `GD-${Date.now()}` };
    await setDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/godowns`, newGodown.id), newGodown);
  }, "Godown added successfully!", "Error adding godown");

  const updateGodown = wrapFirestoreAction(async (id, updatedData) => {
    if (id === SALES_GODOWN_ID) {
        throw new Error("Cannot modify the system-managed 'Sales' godown.");
    }

    const oldGodownName = godowns.find(g => g.id === id)?.name;
    const newGodownName = updatedData.name;

    if (oldGodownName !== newGodownName) {
      const rawCoilQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`), where("godown", "==", oldGodownName));
      const rawCoilSnapshot = await getDocs(rawCoilQuery);
      if (!rawCoilSnapshot.empty) {
        throw new Error(`Cannot rename godown '${oldGodownName}'. It contains raw coils. Please transfer all raw coils out before renaming.`);
      }

      const processedItemQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`), where("godown", "==", oldGodownName));
      const processedItemSnapshot = await getDocs(processedItemQuery);
      if (!processedItemSnapshot.empty) {
        throw new Error(`Cannot rename godown '${oldGodownName}'. It contains processed items. Please transfer all processed items out before renaming.`);
      }
      const existingGodownQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/godowns`), where("name", "==", newGodownName));
      const existingGodownSnapshot = await getDocs(existingGodownQuery);
      if (!existingGodownSnapshot.empty) {
        throw new Error(`A godown named '${newGodownName}' already exists. Please choose a different name.`);
      }
    }
    await updateDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/godowns`, id), updatedData);
  }, "Godown updated successfully!", "Error updating godown");

  const deleteGodown = wrapFirestoreConfirmAction(async (id) => {
    if (id === SALES_GODOWN_ID) {
        throw new Error("Cannot delete the system-managed 'Sales' godown.");
    }
    const godownName = godowns.find(g => g.id === id)?.name;

    const rawCoilQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`), where("godown", "==", godownName));
    const rawCoilSnapshot = await getDocs(rawCoilQuery);
    if (!rawCoilSnapshot.empty) {
      throw new Error("It contains raw coils.");
    }

    const processedItemQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`), where("godown", "==", godownName));
    const processedItemSnapshot = await getDocs(processedItemQuery);
    if (!processedItemSnapshot.empty) {
      throw new Error("It contains processed items.");
    }
    await deleteDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/godowns`, id));
  }, "Are you sure you want to delete this godown? This action cannot be undone.", "Godown deleted successfully!", "Error deleting godown");

  const processCoil = wrapFirestoreAction(async (coilId, processingType, data) => {
    const rawCoilRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, coilId);
    const rawCoilSnap = await getDoc(rawCoilRef);
    if (!rawCoilSnap.exists()) {
      throw new Error("Selected raw coil not found.");
    }
    const rawCoil = { id: rawCoilSnap.id, ...rawCoilSnap.data() };
    let newProcessedItems = [];
    let totalProcessedWeightFromCurrentAction = 0;

    if (processingType === 'sheet') {
      const { length, quantity, godown, actualWeight, actualThickness, processingDate } = data;
      totalProcessedWeightFromCurrentAction = parseFloat(actualWeight);
      newProcessedItems.push({
        parentId: rawCoil.id, type: 'sheet', length, quantity, weight: totalProcessedWeightFromCurrentAction,
        godown, thickness: rawCoil.thickness, actualProcessedThickness: actualThickness,
        width: rawCoil.width, coilId: rawCoil.coilId, timestamp: new Date(processingDate).toISOString(),
      });
    } else if (processingType === 'slit-coil') {
      const { slitConfigurations, godown, processingDate } = data;
      slitConfigurations.forEach(sc => {
        totalProcessedWeightFromCurrentAction += sc.weight;
        newProcessedItems.push({
          parentId: rawCoil.id, type: 'slit-coil', slitWidth: sc.slitWidth, weight: sc.weight,
          godown, thickness: rawCoil.thickness, coilId: rawCoil.coilId, timestamp: new Date(processingDate).toISOString(),
        });
      });
    } else {
      throw new Error("Invalid processing type.");
    }

    const newRemainingWeight = rawCoil.weight - totalProcessedWeightFromCurrentAction;
    let updatedStatus = rawCoil.status;
    if (totalProcessedWeightFromCurrentAction > 0 && rawCoil.status === 'available') {
      updatedStatus = 'partially-processed';
    }

    await updateRawCoil(rawCoil.id, { weight: newRemainingWeight, status: updatedStatus });
    for (const item of newProcessedItems) {
      await addProcessedItem(item);
    }
    return true;
  }, "Coil processed successfully and inventory updated!", "Error processing coil");

  const endCoilProcessing = wrapFirestoreAction(async (coilId) => {
    const rawCoilRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, coilId);
    const rawCoilSnap = await getDoc(rawCoilRef);
    if (!rawCoilSnap.exists()) {
      throw new Error("Selected raw coil not found for ending processing.");
    }
    const rawCoil = { id: rawCoilSnap.id, ...rawCoilSnap.data() };

    const processedItemsQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`), where("parentId", "==", coilId));
    const processedItemsSnap = await getDocs(processedItemsQuery);
    let totalProcessedWeightFromThisCoil = 0;
    processedItemsSnap.forEach(doc => { totalProcessedWeightFromThisCoil += doc.data().weight; });

    const initialCoilWeight = rawCoil.initialWeight;
    const remainingCoilWeightAtEndOfProcessing = rawCoil.weight;
    const weightVariance = totalProcessedWeightFromThisCoil - initialCoilWeight;

    let varianceStatus = 'Exact Match';
    if (Math.abs(weightVariance) > 0.01) {
      varianceStatus = weightVariance > 0 ? 'Excess Weight Received' : 'Shortage of Weight';
    }

    const report = {
      coilId: rawCoil.coilId, rawCoilId: rawCoil.id, initialCoilWeight, finalCoilWeight: remainingCoilWeightAtEndOfProcessing,
      totalProcessedWeight: totalProcessedWeightFromThisCoil, scrapWeight: 0, weightVariance, varianceStatus,
      timestamp: new Date().toISOString(), id: `PR-${Date.now()}`
    };
    await setDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/processingReports`, report.id), report);
    await updateRawCoil(coilId, { status: 'processed-out', weight: 0 });
    return true; // Indicate success for external calls
  }, null, "Error ending coil processing"); // Success message is handled dynamically

  const deleteProcessingReport = wrapFirestoreConfirmAction(async (reportId) => {
    await deleteDoc(doc(db, `/artifacts/${appId}/users/${appDataUserId}/processingReports`, reportId));
  }, "Are you sure you want to delete this processing report? This action cannot be undone.", "Processing report deleted successfully!", "Error deleting processing report");

  const transferStock = wrapFirestoreAction(async (itemType, itemId, fromGodownName, toGodownName, transferQuantity = 0, transferItemWeight = 0) => {
    if (fromGodownName === toGodownName) {
      throw new Error("Cannot transfer to the same godown.");
    }
    if (itemType === 'rawCoil') {
      const itemRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, itemId);
      const itemSnap = await getDoc(itemRef);
      if (!itemSnap.exists()) throw new Error("Raw coil not found for transfer.");
      if (itemSnap.data().godown !== fromGodownName) throw new Error("Raw coil is not in the specified 'From' godown.");
      await updateDoc(itemRef, { godown: toGodownName });
    } else if (itemType === 'sheet' || itemType === 'slitCoil') {
      const itemRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`, itemId);
      const itemSnap = await getDoc(itemRef);
      if (!itemSnap.exists()) throw new Error("Processed item not found for transfer.");
      if (itemSnap.data().godown !== fromGodownName) throw new Error("Processed item is not in the specified 'From' godown.");

      let currentWeight = itemSnap.data().weight || 0;
      let currentQuantity = itemSnap.data().quantity || 0;
      const actualTransferWeight = parseFloat(transferItemWeight);

      if (itemType === 'sheet') {
        const actualTransferQuantity = parseInt(transferQuantity);
        if (actualTransferQuantity <= 0 || actualTransferQuantity > currentQuantity) throw new Error("Invalid quantity for sheet transfer.");
        if (actualTransferWeight <= 0 || actualTransferWeight > currentWeight) throw new Error("Invalid weight for sheet transfer. Must be positive and not exceed current.");
        await updateDoc(itemRef, { quantity: currentQuantity - actualTransferQuantity, weight: currentWeight - actualTransferWeight });
        await addProcessedItem({
          ...itemSnap.data(), id: `${itemSnap.data().type === 'sheet' ? 'PS' : 'SC'}-${Date.now()}-T`,
          godown: toGodownName, quantity: actualTransferQuantity, weight: actualTransferWeight, timestamp: new Date().toISOString()
        });
        if ((currentQuantity - actualTransferQuantity) <= 0.01) await deleteDoc(itemRef);
      } else { // Slit Coil
        if (actualTransferWeight <= 0 || actualTransferWeight > currentWeight) throw new Error("Invalid weight for slit coil transfer. Must be positive and not exceed current.");
        await updateDoc(itemRef, { weight: currentWeight - actualTransferWeight });
        await addProcessedItem({
          ...itemSnap.data(), id: `${itemSnap.data().type === 'sheet' ? 'PS' : 'SC'}-${Date.now()}-T`,
          godown: toGodownName, weight: actualTransferWeight, timestamp: new Date().toISOString()
        });
        if ((currentWeight - actualTransferWeight) <= 0.01) await deleteDoc(itemRef);
      }
    } else if (itemType === 'scrap') {
      throw new Error("Scrap transfer is not supported.");
    }
  }, null, "Error transferring stock"); // Success messages are handled dynamically within the logic

  const updateAppSettings = wrapFirestoreAction(async (settingsData) => {
    const settingsRef = doc(db, `/artifacts/${appId}/public/data/appSettings/general`);
    await setDoc(settingsRef, settingsData, { merge: true });
  }, "Settings updated successfully!", "Error updating settings");

  const revertCoilStatus = wrapFirestoreConfirmAction(async (coilId) => {
    const batch = writeBatch(db);
    const rawCoilRef = doc(db, `/artifacts/${appId}/users/${appDataUserId}/rawCoils`, coilId);
    const rawCoilSnap = await getDoc(rawCoilRef);
    if (!rawCoilSnap.exists()) {
      throw new Error("Raw coil not found.");
    }
    const rawCoil = { id: rawCoilSnap.id, ...rawCoilSnap.data() };
    if (rawCoil.status !== 'processed-out') {
      throw new Error("Only 'processed-out' coils can be reverted.");
    }

    const processedItemsQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processedItems`), where("parentId", "==", coilId));
    const processedItemsSnap = await getDocs(processedItemsQuery);
    processedItemsSnap.forEach(itemDoc => batch.delete(itemDoc.ref));

    const processingReportQuery = query(collection(db, `/artifacts/${appId}/users/${appDataUserId}/processingReports`), where("rawCoilId", "==", coilId));
    const processingReportSnap = await getDocs(processingReportQuery);
    processingReportSnap.forEach(reportDoc => batch.delete(reportDoc.ref));

    batch.update(rawCoilRef, {
      weight: rawCoil.initialWeight,
      status: 'available'
    });
    await batch.commit();
  }, "Are you sure you want to revert this coil to 'available' status? This will delete all associated processed items and reports. The weight and weight variance calculations for the raw coil will also be brought back to actuals.", "Coil reverted successfully!", "Error reverting coil status");


  return (
    <CoilContext.Provider value={{
      rawCoils, processedItems, godowns, processingReports, appSettings, loading,
      firebaseUid, appDataUserId, isAdminLoggedIn,
      addRawCoil, updateRawCoil, deleteRawCoil,
      addProcessedItem, updateProcessedItem, deleteProcessedItem,
      addGodown, updateGodown, deleteGodown,
      processCoil, endCoilProcessing, deleteProcessingReport, transferStock,
      updateAppSettings, revertCoilStatus,
      showModal, hideModal, modal,
      showNotification, notification,
      handleAdminLogin, handleAdminLogout,
      SALES_GODOWN_NAME // Expose SALES_GODOWN_NAME to context
    }}>
      {children}
      {loading && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
            <p className="mt-4 text-white text-lg">
              {(!firebaseUid || !appDataUserId) ? 'Initializing Application...' : 'Processing...'}
            </p>
          </div>
        </div>
      )}
    </CoilContext.Provider>
  );
};

// --- Reusable Notification Component ---
const Notification = () => {
  const { notification } = useContext(CoilContext);
  if (!notification.isOpen) return null;
  const notificationBgColor = notification.type === 'error' ? 'bg-red-500' : notification.type === 'success' ? 'bg-green-500' : 'bg-blue-500';
  return (
    <div className={`fixed top-4 right-4 z-[100] p-4 rounded-lg text-white shadow-lg transition-all duration-300 transform ${notificationBgColor}`}>
      {notification.message}
    </div>
  );
};

// --- Reusable Modal Component ---
const Modal = () => {
  const { modal, hideModal } = useContext(CoilContext);
  if (!modal.isOpen) return null;
  const modalBgColor = modal.type === 'error' ? 'bg-red-100' : modal.type === 'confirm' ? 'bg-yellow-100' : 'bg-blue-100';
  const modalBorderColor = modal.type === 'error' ? 'border-red-400' : modal.type === 'confirm' ? 'border-yellow-400' : 'border-blue-400';
  const modalTextColor = modal.type === 'error' ? 'text-red-700' : modal.type === 'confirm' ? 'text-yellow-700' : 'text-blue-700';
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[100]">
      <div className={`p-6 rounded-lg shadow-xl max-w-sm w-full ${modalBgColor} border ${modalBorderColor}`}>
        <div className={`text-lg font-semibold mb-4 ${modalTextColor}`}>
            {modal.message}
        </div>
        <div className="flex justify-end space-x-3">
          {modal.type === 'confirm' && (
            <button
              onClick={() => { modal.onConfirm && modal.onConfirm(); hideModal(); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow"
            >
              Confirm
            </button>
          )}
          <button
            onClick={hideModal}
            className={`px-4 py-2 ${modal.type === 'confirm' ? 'bg-gray-300 text-gray-800' : 'bg-indigo-600 text-white'} rounded-md hover:bg-gray-400 transition-colors shadow`}
          >
            {modal.type === 'confirm' ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Icons (using simple inline SVGs for this example) ---
const HomeIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-home"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
const PlusIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus"><path d="M12 5v14" /><path d="M5 12h14" /></svg>);
const HammerIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-hammer"><path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0-.83-.83-.83-2.17 0-3L12 9" /><path d="M18 11.5 9.5 3A2.78 2.78 0 0 0 7 5.5l8.5 8.5A2.78 2.78 0 0 0 18 11.5Z" /><path d="m21 21-1.5-1.5" /><path d="m16 16 1.5 1.5" /><path d="m19 14 1.5 1.5" /></svg>);
const PackageIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package"><path d="m7.5 4.27 9 5.15" /><path d="M2.5 8.64 12 14l9.5-5.36L12 3 2.5 8.64Z" /><path d="m12 14 9.5-5.36" /><path d="M12 14.01V19" /><path d="M12 19l-9.5-5.36" /><path d="M2.5 15.36 12 20.64l9.5-5.28" /></svg>);
const WarehouseIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-warehouse"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.66a2 2 0 0 1 1.48 0l8 3.66c.6.27 1.02.82 1.02 1.47Z" /><path d="M10 6v6" /><path d="M14 12V6" /><path d="M10 12h4" /><path d="M12 22V12" /></svg>);
const FileTextIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>);
const BoxIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-box-select"><path d="M21 12H3"/><path d="M12 3v18"/><path d="M7 3h-2a2 2 0 0 0-2 2v2"/><path d="M3 17v2a2 2 0 0 0 2 2h2"/><path d="M17 21h2a2 2 0 0 0 2-2v-2"/><path d="M21 7v-2a2 2 0 0 0-2-2h-2"/></svg>);
const EditIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const SettingsIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.78 1.22a2 2 0 0 0 .73 2.73l.04.02a2 2 0 0 1 .97 1.91V14a2 2 0 0 1-.97 1.91l-.04-.02a2 2 0 0 0-.73 2.73l.78 1.22a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.78-1.22a2 2 0 0 0-.73-2.73l-.04-.02a2 2 0 0 1-.97-1.91V10a2 2 0 0 1 .97-1.91l-.04-.02a2 2 0 0 0 .73-2.73l-.78-1.22a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>);
const CalendarDaysIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar-days"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/></svg>);
const SearchIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>);
const DeleteIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>);


// --- Login Page Component ---
const LoginPage = () => {
    const { handleAdminLogin, showNotification } = useContext(CoilContext);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const ADMIN_USERNAME = "admin";
    const ADMIN_PASSWORD = "pass";
    const ADMIN_DATA_UID = "fixed_admin_data_id";

    const handleSubmit = (e) => {
        e.preventDefault();
        if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
            handleAdminLogin(ADMIN_DATA_UID);
            showNotification("Login successful!", "success");
        } else {
            showNotification("Invalid username or password.", "error");
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 p-4">
            <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full border border-indigo-200">
                <h1 className="text-3xl font-extrabold mb-6 text-center text-indigo-800">CoilFlow Login</h1>
                <p className="text-sm text-gray-600 text-center mb-6">
                    Use username: <strong className="text-indigo-700">admin</strong> and password: <strong className="text-indigo-700">pass</strong>
                </p>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="username" className="block text-gray-700 text-sm font-bold mb-2">Username:</label>
                        <input
                            type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)}
                            className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                            placeholder="admin" required
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">Password:</label>
                        <input
                            type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                            placeholder="pass" required
                        />
                    </div>
                    <div className="flex justify-center">
                        <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">
                            Login
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Reusable Searchable Input Component ---
const SearchableItemInput = ({ label, options, value, onChange, displayKey, filterKeys, placeholder, required = false, disabled = false }) => {
    const [searchText, setSearchText] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (value) {
            const selectedOption = options.find(option => option.id === value);
            if (selectedOption) setSearchText(displayKey(selectedOption));
        } else {
            setSearchText('');
        }
    }, [value, options, displayKey]);

    const filteredOptions = useMemo(() => {
        if (!searchText) return options;
        const lowerCaseSearchText = searchText.toLowerCase();
        return options.filter(option =>
            filterKeys.some(key => {
                const val = option[key];
                return (typeof val === 'string' && val.toLowerCase().includes(lowerCaseSearchText)) ||
                       (typeof val === 'number' && String(val).includes(lowerCaseSearchText));
            })
        );
    }, [searchText, options, filterKeys]);

    const handleSelect = (optionId) => {
        onChange(optionId);
        const selectedOption = options.find(opt => opt.id === optionId);
        setSearchText(selectedOption ? displayKey(selectedOption) : '');
        setIsOpen(false);
    };

    const handleClickOutside = useCallback((event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setIsOpen(false);
            if (value === null || !options.some(opt => opt.id === value)) {
                if (!options.some(opt => displayKey(opt).toLowerCase() === searchText.toLowerCase())) {
                    setSearchText('');
                }
            }
        }
    }, [options, value, searchText, displayKey]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    return (
        <div className="relative" ref={wrapperRef}>
            <label htmlFor={label.replace(/\s/g, '')} className="block text-gray-700 text-sm font-bold mb-2">{label}:</label>
            <input
                type="text" id={label.replace(/\s/g, '')} value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setIsOpen(true); onChange(null); }}
                onFocus={() => setIsOpen(true)}
                className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                placeholder={placeholder} required={required} disabled={disabled}
            />
            {isOpen && filteredOptions.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                    {filteredOptions.map(option => (
                        <li key={option.id} className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-gray-800 text-sm" onClick={() => handleSelect(option.id)}>
                            {displayKey(option)}
                        </li>
                    ))}
                </ul>
            )}
             {isOpen && filteredOptions.length === 0 && searchText && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 py-2 px-4 text-gray-500 text-sm shadow-lg">
                    No matching items found.
                </div>
            )}
        </div>
    );
};


// --- Page Components ---
const Dashboard = ({ setCurrentPage }) => {
  const { rawCoils, processedItems, godowns, appDataUserId, SALES_GODOWN_NAME } = useContext(CoilContext);

  // Filter out items in the "Sales" godown for total weight calculations
  const activeRawCoils = rawCoils.filter(coil => coil.status !== 'processed-out' && coil.godown !== SALES_GODOWN_NAME);
  const activeProcessedItems = processedItems.filter(item => item.godown !== SALES_GODOWN_NAME);

  const totalRawCoilsWeightInInventory = activeRawCoils.reduce((sum, coil) => sum + coil.weight, 0);
  const totalProcessedSheetsWeightInInventory = activeProcessedItems.filter(item => item.type === 'sheet').reduce((sum, item) => sum + item.weight, 0);
  const totalSlitCoilsWeightInInventory = activeProcessedItems.filter(item => item.type === 'slit-coil').reduce((sum, item) => sum + item.weight, 0);


  // Filter out godowns containing 'Sales' in their name (case-insensitive) for Godown Overview
  const filteredGodowns = godowns.filter(g => g.name !== SALES_GODOWN_NAME);

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-indigo-50 to-purple-50 min-h-screen">
      <h1 className="text-4xl font-extrabold mb-8 text-indigo-800 text-center drop-shadow-md">Dashboard</h1>
      <div className="text-sm text-gray-600 mb-6 text-center">
        Logged in as: <span className="font-mono bg-gray-200 px-2 py-1 rounded-md text-sm">admin ({appDataUserId || 'N/A'})</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        <DashboardCard title="Total Raw Coil Weight (Excluding Sales)" value={`${totalRawCoilsWeightInInventory.toFixed(2)} kg`} icon={<PackageIcon />} color="bg-blue-100 text-blue-800" hoverColor="hover:bg-blue-200" onClick={() => setCurrentPage('inventory')} />
        <DashboardCard title="Total Processed Sheet Weight (Excluding Sales)" value={`${totalProcessedSheetsWeightInInventory.toFixed(2)} kg`} icon={<HammerIcon />} color="bg-green-100 text-green-800" hoverColor="hover:bg-green-200" onClick={() => setCurrentPage('inventory')} />
        <DashboardCard title="Total Slit Coil Weight (Excluding Sales)" value={`${totalSlitCoilsWeightInInventory.toFixed(2)} kg`} icon={<HammerIcon />} color="bg-purple-100 text-purple-800" hoverColor="hover:bg-purple-200" onClick={() => setCurrentPage('inventory')} />
      </div>

      <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <ActionButton text="Add New Coil" icon={<PlusIcon />} onClick={() => setCurrentPage('coil-input')} color="bg-indigo-600 hover:bg-indigo-700" />
          <ActionButton text="Process Coil" icon={<HammerIcon />} onClick={() => setCurrentPage('processing')} color="bg-purple-600 hover:bg-purple-700" />
          <ActionButton text="View Inventory" icon={<PackageIcon />} onClick={() => setCurrentPage('inventory')} color="bg-teal-600 hover:bg-teal-700" />
          <ActionButton text="Manage Godowns" icon={<WarehouseIcon />} onClick={() => setCurrentPage('godown-management')} color="bg-red-500 hover:bg-red-600" />
          <ActionButton text="Processing Reports" icon={<FileTextIcon />} onClick={() => setCurrentPage('processing-reports')} color="bg-orange-500 hover:bg-orange-600" />
          <ActionButton text="Stock Transfer" icon={<BoxIcon />} onClick={() => setCurrentPage('stock-transfer')} color="bg-sky-600 hover:bg-sky-700" />
          <ActionButton text="Monthly Coil Report" icon={<CalendarDaysIcon />} onClick={() => setCurrentPage('monthly-coil-report')} color="bg-gray-600 hover:bg-gray-700" />
          <ActionButton text="Coil Details" icon={<SearchIcon />} onClick={() => setCurrentPage('coil-details')} color="bg-yellow-600 hover:bg-yellow-700" />
          <ActionButton text="Settings" icon={<SettingsIcon />} onClick={() => setCurrentPage('settings')} color="bg-blue-600 hover:bg-blue-700" />
        </div>
      </div>

      <div className="mt-12">
        <h2 className="text-3xl font-extrabold mb-8 text-indigo-800 text-center drop-shadow-md">Godown Overview (Excluding Sales)</h2>
        {filteredGodowns.length === 0 ? (
          <p className="text-center text-gray-600 text-lg">No godowns configured or displayed.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredGodowns.map(godown => {
              const totalRawCoilsWeightInThisGodown = rawCoils
                .filter(coil => coil.godown === godown.name && coil.status !== 'processed-out')
                .reduce((sum, coil) => sum + coil.weight, 0);

              const totalProcessedSheetsWeightInThisGodown = processedItems
                .filter(item => item.godown === godown.name && item.type === 'sheet')
                .reduce((sum, item) => sum + item.weight, 0);

              const totalSlitCoilsWeightInThisGodown = processedItems
                .filter(item => item.godown === godown.name && item.type === 'slit-coil')
                .reduce((sum, item) => sum + item.weight, 0);

              return (
                <div key={godown.id} className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{godown.name}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{godown.location}</p>
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-700 mb-2">Inventory Summary:</h4>
                    <p className="text-gray-700 text-sm">Total Raw Coil Weight: <strong className="font-medium text-blue-700">{totalRawCoilsWeightInThisGodown.toFixed(2)} kg</strong></p>
                    <p className="text-gray-700 text-sm">Total Sheet Weight: <strong className="font-medium text-green-700">{totalProcessedSheetsWeightInThisGodown.toFixed(2)} kg</strong></p>
                    <p className="text-gray-700 text-sm">Total Slit Coil Weight: <strong className="font-medium text-purple-700">{totalSlitCoilsWeightInThisGodown.toFixed(2)} kg</strong></p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DashboardCard = ({ title, value, icon, color, hoverColor, onClick }) => (
  <div onClick={onClick} className={`flex flex-col items-center p-6 rounded-xl shadow-md transition-all duration-300 transform hover:scale-105 cursor-pointer ${color} ${hoverColor} border border-opacity-30`}>
    <div className="text-4xl mb-3">{icon}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-4xl font-bold">{value}</p>
  </div>
);

const ActionButton = ({ text, icon, onClick, color }) => (
  <button onClick={onClick} className={`flex items-center justify-center p-4 rounded-lg text-white font-semibold text-lg shadow-md transition-all duration-300 transform hover:-translate-y-1 ${color}`}>
    {icon}
    <span className="ml-3">{text}</span>
  </button>
);


const CoilInput = () => {
  const { addRawCoil, godowns, showModal, SALES_GODOWN_NAME } = useContext(CoilContext);
  const [coilData, setCoilData] = useState({ coilId: '', thickness: '', width: '', weight: '', godown: '', receivedMonth: new Date().toISOString().substring(0, 7) });

  const handleChange = (e) => setCoilData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { coilId, thickness, width, weight, godown, receivedMonth } = coilData;
    if (!coilId || !thickness || !width || !weight || !godown || !receivedMonth) { showModal("Please fill in all fields.", "error"); return; }
    if (isNaN(thickness) || isNaN(width) || isNaN(weight) || parseFloat(thickness) <= 0 || parseFloat(width) <= 0 || parseFloat(weight) <= 0) { showModal("Thickness, width, and weight must be positive numbers.", "error"); return; }
    if (godown === SALES_GODOWN_NAME) { showModal(`Cannot add raw coils directly to the "${SALES_GODOWN_NAME}" godown. Please use stock transfer.`, "error"); return; }


    await addRawCoil({ coilId, thickness: parseFloat(thickness), width: parseFloat(width), weight: parseFloat(weight), godown, receivedMonth });
    setCoilData({ coilId: '', thickness: '', width: '', weight: '', godown: '', receivedMonth: new Date().toISOString().substring(0, 7) });
  };

  const availableGodowns = godowns.filter(g => g.name !== SALES_GODOWN_NAME);

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <h1 className="text-4xl font-extrabold mb-8 text-indigo-800 text-center drop-shadow-md">Add New Raw Coil</h1>
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Input label="Coil ID" name="coilId" value={coilData.coilId} onChange={handleChange} placeholder="e.g., CIL-2023-001" type="text" required />
          <Input label="Thickness (mm)" name="thickness" value={coilData.thickness} onChange={handleChange} placeholder="e.g., 1.5" type="number" step="0.01" required />
          <Input label="Width (mm)" name="width" value={coilData.width} onChange={handleChange} placeholder="e.g., 1250" type="number" step="0.01" required />
          <Input label="Weight (kg)" name="weight" value={coilData.weight} onChange={handleChange} placeholder="e.g., 5000" type="number" step="0.01" required />
          <div>
            <label htmlFor="godown" className="block text-gray-700 text-sm font-bold mb-2">
              Godown:
            </label>
            <select
              id="godown"
              name="godown"
              value={coilData.godown}
              onChange={handleChange}
              className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              required
            >
              <option value="">Select a Godown</option>
              {availableGodowns.map(g => (
                <option key={g.id} value={g.name}>{g.name}</option>
              ))}
            </select>
            {availableGodowns.length === 0 && (
              <p className="text-red-500 text-xs italic mt-2">No standard godowns available. Please add some via Godown Management.</p>
            )}
          </div>
          <Input label="Received Month" name="receivedMonth" value={coilData.receivedMonth} onChange={handleChange} type="month" required />
        </div>
        <div className="flex justify-center">
          <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Add Coil</button>
        </div>
      </form>
    </div>
  );
};

const Input = ({ label, name, value, onChange, placeholder, type = "text", step, min, required = false }) => (
  <div>
    <label htmlFor={name} className="block text-gray-700 text-sm font-bold mb-2">{label}:</label>
    <input
      type={type} id={name} name={name} value={value} onChange={onChange} step={step} min={min}
      className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
      placeholder={placeholder} required={required}
    />
  </div>
);


const Processing = ({ preSelectedCoilId, setCurrentPage }) => {
  const { rawCoils, processCoil, endCoilProcessing, godowns, showModal, SALES_GODOWN_NAME } = useContext(CoilContext);
  const [selectedCoilId, setSelectedCoilId] = useState(preSelectedCoilId || '');
  const [processingType, setProcessingType] = useState('sheet');
  const [sheetData, setSheetData] = useState({ length: '', quantity: '', actualWeight: '', actualThickness: '' });
  const [slitCoilsData, setSlitCoilsData] = useState([{ slitWidth: '', weight: '' }]);
  const [targetGodown, setTargetGodown] = useState('');
  const [processingDate, setProcessingDate] = useState(new Date().toISOString().substring(0, 10));

  const availableCoils = rawCoils.filter(coil => coil.status === 'available' || coil.status === 'partially-processed');
  const selectedCoil = availableCoils.find(coil => coil.id === selectedCoilId);

  useEffect(() => { if (preSelectedCoilId) setSelectedCoilId(preSelectedCoilId); }, [preSelectedCoilId]);
  useEffect(() => {
    // Only set a default godown if it's not the Sales godown and there are other godowns
    if (godowns.length > 0 && !targetGodown) {
        const nonSalesGodowns = godowns.filter(g => g.name !== SALES_GODOWN_NAME);
        if (nonSalesGodowns.length > 0) {
            setTargetGodown(nonSalesGodowns[0].name);
        } else {
            setTargetGodown(''); // No non-sales godowns available
        }
    }
  }, [godowns, targetGodown, SALES_GODOWN_NAME]);

  useEffect(() => { if (selectedCoil && processingType === 'sheet') setSheetData(prev => ({ ...prev, actualThickness: selectedCoil.thickness })); else setSheetData(prev => ({ ...prev, actualThickness: '' })); }, [selectedCoil, processingType]);

  useEffect(() => {
    if (processingType === 'sheet' && selectedCoil && sheetData.actualThickness && sheetData.length && sheetData.quantity) {
      const { actualThickness, length, quantity } = sheetData;
      const { width } = selectedCoil;
      if (parseFloat(actualThickness) > 0 && width > 0 && parseFloat(length) > 0 && parseInt(quantity) > 0) {
        setSheetData(prev => ({ ...prev, actualWeight: (parseFloat(actualThickness) * width * parseFloat(length) * 0.00000785 * parseInt(quantity)).toFixed(2) }));
      } else {
        setSheetData(prev => ({ ...prev, actualWeight: '' }));
      }
    } else if (processingType === 'sheet' && !sheetData.length && !sheetData.quantity) {
      setSheetData(prev => ({ ...prev, actualWeight: '' }));
    }
  }, [selectedCoil, sheetData.actualThickness, sheetData.length, sheetData.quantity, processingType]);

  const handleSheetChange = (e) => setSheetData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSlitCoilChange = (index, e) => {
    const newSlitCoils = [...slitCoilsData];
    newSlitCoils[index][e.target.name] = e.target.value;
    setSlitCoilsData(newSlitCoils);
  };

  const addSlitCoilRow = () => setSlitCoilsData(prev => [...prev, { slitWidth: '', weight: '' }]);
  const removeSlitCoilRow = (index) => setSlitCoilsData(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCoilId) { showModal("Please select a raw coil to process.", "error"); return; }
    if (!targetGodown) { showModal("Please select a target godown for processed items.", "error"); return; }
    if (!processingDate) { showModal("Please select a processing date.", "error"); return; }
    if (targetGodown === SALES_GODOWN_NAME) { showModal(`Cannot process items directly into the "${SALES_GODOWN_NAME}" godown. Please process to a standard godown and then use stock transfer.`, "error"); return; }


    let success = false;
    if (processingType === 'sheet') {
      const { length, quantity, actualWeight, actualThickness } = sheetData;
      if (!length || !quantity || actualWeight === '' || actualThickness === '') { showModal("Please enter sheet length, quantity, actual thickness, and total weight.", "error"); return; }
      if (isNaN(length) || isNaN(quantity) || isNaN(actualWeight) || isNaN(actualThickness) || parseFloat(length) <= 0 || parseInt(quantity) <= 0 || parseFloat(actualWeight) <= 0 || parseFloat(actualThickness) <= 0) { showModal("Sheet length, quantity, actual thickness, and weight must be positive numbers.", "error"); return; }
      success = await processCoil(selectedCoilId, 'sheet', { length: parseFloat(length), quantity: parseInt(quantity), actualWeight: parseFloat(actualWeight), actualThickness: parseFloat(actualThickness), godown: targetGodown, processingDate });
      if (success) setSheetData({ length: '', quantity: '', actualWeight: '', actualThickness: '' });
    } else {
      if (slitCoilsData.length === 0) { showModal("Please add at least one slit coil configuration.", "error"); return; }
      for (const sc of slitCoilsData) {
        if (!sc.slitWidth || !sc.weight) { showModal("Please enter slit width and weight for all slit coils.", "error"); return; }
        if (isNaN(sc.slitWidth) || isNaN(sc.weight) || parseFloat(sc.slitWidth) <= 0 || parseFloat(sc.weight) <= 0) { showModal("Slit width and weight must be positive numbers.", "error"); return; }
        if (selectedCoil && parseFloat(sc.slitWidth) > selectedCoil.width) { showModal(`Slit width ${sc.slitWidth}mm cannot be greater than raw coil width ${selectedCoil.width}mm.`, "error"); return; }
      }
      success = await processCoil(selectedCoilId, 'slit-coil', { slitConfigurations: slitCoilsData.map(sc => ({ slitWidth: parseFloat(sc.slitWidth), weight: parseFloat(sc.weight) })), godown: targetGodown, processingDate });
      if (success) setSlitCoilsData([{ slitWidth: '', weight: '' }]);
    }
    if (success) setSelectedCoilId('');
    if (setCurrentPage && success) setCurrentPage('coil-details', { preSelectedCoilId: selectedCoilId });
  };

  const handleEndProcessing = async () => {
    if (!selectedCoilId) { showModal("Please select a raw coil to end processing.", "error"); return; }
    const currentCoil = rawCoils.find(c => c.id === selectedCoilId);
    if (!currentCoil) { showModal("Selected coil not found.", "error"); return; }
    if (currentCoil.status === 'processed-out') { showModal("This coil is already marked as fully processed.", "info"); return; }

    const message = `End Processing for Coil: ${currentCoil.coilId}\n\nRemaining coil weight: ${currentCoil.weight?.toFixed(2) || '0.00'} kg.\nThis remaining weight will be accounted for in the variance calculation as a shortage if not fully processed.\n\nWarning: This action will mark the coil as 'processed-out' and cannot be undone.`;
    showModal(message, 'confirm', async () => {
      const success = await endCoilProcessing(selectedCoilId);
      if (success) { setSelectedCoilId(''); setCurrentPage('coil-details', { preSelectedCoilId: selectedCoilId }); }
    });
  };

  const nonSalesGodowns = godowns.filter(g => g.name !== SALES_GODOWN_NAME);

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-green-50 to-lime-100 min-h-screen">
      <h1 className="text-4xl font-extrabold mb-8 text-green-800 text-center drop-shadow-md">Process Raw Coil</h1>
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto border border-gray-200">
        <div className="mb-6">
          <SearchableItemInput label="Select Raw Coil" options={availableCoils} value={selectedCoilId} onChange={setSelectedCoilId}
            displayKey={(coil) => `${coil.coilId} (${coil.thickness}mm x ${coil.width}mm, ${coil.weight.toFixed(2)}kg, Godown: ${coil.godown})`}
            filterKeys={['coilId', 'thickness', 'width', 'weight', 'godown']} placeholder="Search by Coil ID, Thickness, Weight, Godown..." required={true}
          />
          {availableCoils.length === 0 && (<p className="text-red-500 text-xs italic mt-2">No raw coils available for processing. Add coils first.</p>)}
        </div>

        {selectedCoil && (
          <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Selected Coil Details:</h3>
            <p><strong>Coil ID:</strong> {selectedCoil.coilId}</p><p><strong>Dimensions:</strong> {selectedCoil.thickness}mm (T) x {selectedCoil.width}mm (W)</p>
            <p><strong>Current Weight:</strong> {selectedCoil.weight.toFixed(2)} kg</p><p><strong>Godown:</strong> {selectedCoil.godown}</p>
            <p><strong>Status:</strong> <span className={`font-medium ${selectedCoil.status === 'available' ? 'text-green-600' : 'text-orange-600'}`}>{selectedCoil.status.replace('-', ' ')}</span></p>
          </div>
        )}
        <Input label="Processing Date" name="processingDate" value={processingDate} onChange={(e) => setProcessingDate(e.target.value)} type="date" required />
        <div className="mb-6">
          <label htmlFor="processingType" className="block text-gray-700 text-sm font-bold mb-2">Processing Type:</label>
          <select id="processingType" value={processingType} onChange={(e) => setProcessingType(e.target.value)} className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200">
            <option value="sheet">Cut to Length (Sheets)</option><option value="slit-coil">Slit Coils</option>
          </select>
        </div>
        <div className="mb-6">
          <label htmlFor="targetGodown" className="block text-gray-700 text-sm font-bold mb-2">Target Godown for Processed Items:</label>
          <select id="targetGodown" value={targetGodown} onChange={(e) => setTargetGodown(e.target.value)} className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" required>
            <option value="">Select Target Godown</option>
            {nonSalesGodowns.map(g => (<option key={g.id} value={g.name}>{g.name}</option>))}
          </select>
          {nonSalesGodowns.length === 0 && (
            <p className="text-red-500 text-xs italic mt-2">No standard godowns available. Processed items cannot go to "Sales" godown.</p>
          )}
        </div>

        {processingType === 'sheet' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input label="Sheet Length (mm)" name="length" value={sheetData.length} onChange={handleSheetChange} placeholder="e.g., 2500" type="number" step="0.01" required />
            <Input label="Quantity" name="quantity" value={sheetData.quantity} onChange={handleSheetChange} placeholder="e.g., 10" type="number" step="1" min="1" required />
            <div className="md:col-span-2">
              <Input label="Actual Thickness (mm) - For Calculation" name="actualThickness" value={sheetData.actualThickness} onChange={handleSheetChange} placeholder="e.g., 1.52" type="number" step="0.01" required />
              <p className="text-gray-500 text-xs italic mt-1">Used for estimated weight calculation. Defaults to coil thickness.</p>
            </div>
            <div className="md:col-span-2">
              <Input label="Total Weight (kg) - Editable" name="actualWeight" value={sheetData.actualWeight} onChange={handleSheetChange} placeholder="Calculated or Actual Weight" type="number" step="0.01" required />
              <p className="text-gray-500 text-xs italic mt-1">Estimated weight is pre-filled. Adjust if actual weight differs.</p>
            </div>
          </div>
        )}

        {processingType === 'slit-coil' && (
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-4 text-gray-700">Slit Coil Configurations:</h3>
            {slitCoilsData.map((sc, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                <Input label="Slit Width (mm)" name="slitWidth" value={sc.slitWidth} onChange={(e) => handleSlitCoilChange(index, e)} placeholder="e.g., 300" type="number" step="0.01" required />
                <Input label="Weight (kg)" name="weight" value={sc.weight} onChange={(e) => handleSlitCoilChange(index, e)} placeholder="e.g., 1000" type="number" step="0.01" required />
                <div className="flex items-end">
                  <button type="button" onClick={() => removeSlitCoilRow(index)} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline transition-all duration-300 shadow">Remove</button>
                </div>
              </div>
            ))}
            <button type="button" onClick={addSlitCoilRow} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline transition-all duration-300 shadow mt-2">Add Slit Coil</button>
          </div>
        )}

        <div className="flex justify-center space-x-4 mt-6">
          <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Process Coil</button>
          {selectedCoil && selectedCoil.status !== 'processed-out' && (
            <button type="button" onClick={handleEndProcessing} className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">End Processing for Coil</button>
          )}
        </div>
      </form>
    </div>
  );
};

const ProcessedItemsDisplay = ({ parentCoilId }) => {
    const { processedItems } = useContext(CoilContext);
    const items = processedItems.filter(item => item.parentId === parentCoilId);
    if (items.length === 0) return (<p className="text-gray-500 text-sm italic mt-2 ml-4">No processed items found for this coil.</p>);
    return (
        <div className="bg-gray-50 p-4 rounded-md mt-2 ml-4 border border-gray-200">
            <h4 className="text-md font-semibold mb-2 text-gray-700">Processed Outputs:</h4>
            <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                {items.map(item => (
                    <li key={item.id}>
                        <strong>{item.type === 'sheet' ? 'Sheet' : 'Slit Coil'}</strong>: {' '}
                        {item.type === 'sheet' ? `${item.actualProcessedThickness?.toFixed(2) || 'N/A'}x${item.width?.toFixed(2) || 'N/A'}x${item.length?.toFixed(2) || 'N/A'} (${item.quantity || 1} Pcs)` : `${item.slitWidth?.toFixed(2) || 'N/A'}mm Slit W`}, {item.weight?.toFixed(2) || 'N/A'}kg (Godown: {item.godown}) - {new Date(item.timestamp).toLocaleDateString()}
                    </li>
                ))}
            </ul>
        </div>
    );
};


const Inventory = () => {
  const { rawCoils, processedItems, godowns, deleteRawCoil, deleteProcessedItem, SALES_GODOWN_NAME } = useContext(CoilContext);
  const [filterGodown, setFilterGodown] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [minThickness, setMinThickness] = useState('');
  const [maxThickness, setMaxThickness] = useState('');
  const [rawCoilSortConfig, setRawCoilSortConfig] = useState({ key: null, direction: 'ascending' });
  const [processedItemSortConfig, setProcessedItemSortConfig] = useState({ key: null, direction: 'ascending' });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  // Filter raw coils: Exclude processed-out. Also exclude Sales godown by default.
  const activeRawCoils = useMemo(() => {
    let filtered = rawCoils.filter(coil => coil.status !== 'processed-out');
    if (filterGodown !== SALES_GODOWN_NAME) { // If not explicitly filtering for Sales, exclude it
        filtered = filtered.filter(coil => coil.godown !== SALES_GODOWN_NAME);
    }
    return filtered;
  }, [rawCoils, filterGodown, SALES_GODOWN_NAME]);

  // Filter processed items: Exclude Sales godown by default.
  const activeProcessedItems = useMemo(() => {
    let filtered = processedItems;
    if (filterGodown !== SALES_GODOWN_NAME) { // If not explicitly filtering for Sales, exclude it
        filtered = filtered.filter(item => item.godown !== SALES_GODOWN_NAME);
    }
    return filtered;
  }, [processedItems, filterGodown, SALES_GODOWN_NAME]);


  const filteredRawCoils = activeRawCoils.filter(coil =>
    (filterGodown === 'All' || coil.godown === filterGodown) &&
    (filterType === 'All' || filterType === 'raw') &&
    (minThickness === '' || coil.thickness >= parseFloat(minThickness)) &&
    (maxThickness === '' || coil.thickness <= parseFloat(maxThickness))
  ).sort((a, b) => {
    if (rawCoilSortConfig.key) {
      const aValue = a[rawCoilSortConfig.key]; const bValue = b[rawCoilSortConfig.key];
      if (aValue < bValue) return rawCoilSortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return rawCoilSortConfig.direction === 'ascending' ? 1 : -1;
    } return 0;
  });

  const filteredProcessedItems = activeProcessedItems.filter(item =>
    (filterGodown === 'All' || item.godown === filterGodown) &&
    (filterType === 'All' || item.type === filterType) &&
    (minThickness === '' || item.thickness >= parseFloat(minThickness)) &&
    (maxThickness === '' || item.thickness <= parseFloat(maxThickness))
  ).sort((a, b) => {
    if (processedItemSortConfig.key) {
      const aValue = a[processedItemSortConfig.key]; const bValue = b[processedItemSortConfig.key];
      if (aValue < bValue) return processedItemSortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return processedItemSortConfig.direction === 'ascending' ? 1 : -1;
    } return 0;
  });

  const totalRawCoilsWeight = filteredRawCoils.reduce((sum, coil) => sum + coil.weight, 0);
  const totalProcessedMaterialWeight = filteredProcessedItems.reduce((sum, item) => sum + item.weight, 0);
  const combinedInventoryTotalWeight = totalRawCoilsWeight + totalProcessedMaterialWeight;


  const handleEditClick = (type, item) => { setItemToEdit({ type, data: item }); setEditModalOpen(true); };

  const requestRawCoilSort = (key) => { setRawCoilSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending' })); };
  const requestProcessedItemSort = (key) => { setProcessedItemSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending' })); };

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-purple-50 to-pink-100 min-h-screen">
      <h1 className="text-4xl font-extrabold mb-8 text-purple-800 text-center drop-shadow-md">Inventory Management</h1>
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select label="Filter by Godown" value={filterGodown} onChange={(e) => setFilterGodown(e.target.value)} options={[
            { value: 'All', label: 'All Standard Godowns' },
            ...godowns.filter(g => g.name !== SALES_GODOWN_NAME).map(g => ({ value: g.name, label: g.name })),
            { value: SALES_GODOWN_NAME, label: SALES_GODOWN_NAME } // Explicitly add Sales Godown filter option
          ]} />
          <Select label="Filter by Item Type" value={filterType} onChange={(e) => setFilterType(e.target.value)} options={[{ value: 'All', label: 'All Types' }, { value: 'raw', label: 'Raw Coils' }, { value: 'sheet', label: 'Sheets' }, { value: 'slit-coil', label: 'Slit Coils' }]} />
          <Input label="Min Thickness (mm)" value={minThickness} onChange={(e) => setMinThickness(e.target.value)} placeholder="e.g., 0.5" type="number" step="0.01" />
          <Input label="Max Thickness (mm)" value={maxThickness} onChange={(e) => setMaxThickness(e.target.value)} placeholder="e.g., 2.0" type="number" step="0.01" />
        </div>
      </div>

      {(filterType === 'All' || filterType === 'raw') && (
        <div className="mb-12 bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold text-gray-800">Raw Coils ({filteredRawCoils.length})</h2></div>
          {filteredRawCoils.length === 0 ? (<p className="text-gray-600">No raw coils found matching the filters.</p>) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <TableHeader col="Coil ID" sortConfig={rawCoilSortConfig} requestSort={requestRawCoilSort} sortKey="coilId" />
                    <TableHeader col="Thickness (mm)" sortConfig={rawCoilSortConfig} requestSort={requestRawCoilSort} sortKey="thickness" />
                    <TableHeader col="Width (mm)" sortConfig={rawCoilSortConfig} requestSort={rawCoilSortConfig} sortKey="width" />
                    <TableHeader col="Weight (kg)" sortConfig={rawCoilSortConfig} requestSort={rawCoilSortConfig} sortKey="weight" />
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Godown</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Status</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRawCoils.map(coil => (
                    <tr key={coil.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800 text-sm">{coil.coilId}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{coil.thickness.toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{coil.width.toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{coil.weight.toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{coil.godown}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${coil.status === 'available' ? 'bg-green-100 text-green-800' : coil.status === 'partially-processed' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                          {coil.status.replace('-', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-800 text-sm flex space-x-2">
                        <button onClick={() => handleEditClick('rawCoil', coil)} className="text-blue-600 hover:text-blue-800 transition-colors duration-200" title="Edit Raw Coil"><EditIcon /></button>
                        <button onClick={() => deleteRawCoil(coil.id)} className="text-red-600 hover:text-red-800 transition-colors duration-200" title="Delete Raw Coil"><DeleteIcon /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 p-4 bg-gray-100 rounded-lg text-gray-800 font-semibold">Total Raw Coils Weight (Filtered): {totalRawCoilsWeight.toFixed(2)} kg</div>
            </div>
          )}
        </div>
      )}

      {(filterType === 'All' || filterType === 'sheet' || filterType === 'slit-coil') && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold text-gray-800">Processed Items ({filteredProcessedItems.length})</h2></div>
          {filteredProcessedItems.length === 0 ? (<p className="text-gray-600">No processed items found matching the filters.</p>) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <TableHeader col="Item ID" sortConfig={processedItemSortConfig} requestSort={requestProcessedItemSort} sortKey="id" />
                    <TableHeader col="Type" sortConfig={processedItemSortConfig} requestSort={requestProcessedItemSort} sortKey="type" />
                    <TableHeader col="Original Coil ID" sortConfig={processedItemSortConfig} requestSort={processedItemSortConfig} sortKey="coilId" />
                    <TableHeader col="Thickness (mm)" sortConfig={processedItemSortConfig} requestSort={processedItemSortConfig} sortKey="thickness" />
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Dimensions</th>
                    <TableHeader col="Quantity" sortConfig={processedItemSortConfig} requestSort={processedItemSortConfig} sortKey="quantity" />
                    <TableHeader col="Weight (kg)" sortConfig={processedItemSortConfig} requestSort={processedItemSortConfig} sortKey="weight" />
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Godown</th>
                    <TableHeader col="Date Processed" sortConfig={processedItemSortConfig} requestSort={processedItemSortConfig} sortKey="timestamp" />
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProcessedItems.map(item => (
                    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.id}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.type === 'sheet' ? 'bg-indigo-100 text-indigo-800' : 'bg-pink-100 text-pink-800'}`}>
                          {item.type === 'sheet' ? 'Sheet' : 'Slit Coil'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.coilId}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.thickness?.toFixed(2) || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">
                        {item.type === 'sheet' ? `${item.actualProcessedThickness?.toFixed(2) || 'N/A'}x${item.width?.toFixed(2) || 'N/A'}x${item.length?.toFixed(2) || 'N/A'}` : `${item.slitWidth?.toFixed(2) || 'N/A'}mm (Slit W)`}
                      </td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.type === 'sheet' ? item.quantity || 'N/A' : 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.weight?.toFixed(2) || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.godown}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm">{item.timestamp ? new Date(item.timestamp).toLocaleDateString() : 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-800 text-sm flex space-x-2">
                        <button onClick={() => handleEditClick('processedItem', item)} className="text-blue-600 hover:text-blue-800 transition-colors duration-200" title="Edit Processed Item"><EditIcon /></button>
                        <button onClick={() => deleteProcessedItem(item.id)} className="text-red-600 hover:text-red-800 transition-colors duration-200" title="Delete Processed Item"><DeleteIcon /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 p-4 bg-gray-100 rounded-lg text-gray-800 font-semibold">Total Processed Material Weight (Filtered): {totalProcessedMaterialWeight.toFixed(2)} kg</div>
            </div>
          )}
           <div className="mt-6 p-4 bg-purple-100 rounded-lg text-purple-800 font-bold text-lg text-center">Combined Inventory Total Weight (Filtered): {combinedInventoryTotalWeight.toFixed(2)} kg</div>
        </div>
      )}

      {editModalOpen && (<EditItemModal item={itemToEdit} onClose={() => setEditModalOpen(false)} godowns={godowns} />)}
    </div>
  );
};

const TableHeader = ({ col, sortConfig, requestSort, sortKey }) => (
    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort(sortKey)}>
        {col} {sortConfig.key === sortKey ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
    </th>
);

const Select = ({ label, value, onChange, options }) => (
    <div>
        <label htmlFor={label.replace(/\s/g, '')} className="block text-gray-700 text-sm font-bold mb-2">{label}:</label>
        <select id={label.replace(/\s/g, '')} value={value} onChange={onChange} className="shadow-sm appearance-none border rounded-md w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200">
            {options.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
        </select>
    </div>
);


const GodownManagement = () => {
  const { godowns, addGodown, updateGodown, deleteGodown, showModal, SALES_GODOWN_NAME, SALES_GODOWN_ID } = useContext(CoilContext);
  const [newGodownName, setNewGodownName] = useState('');
  const [newGodownLocation, setNewGodownLocation] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [godownToEdit, setGodownToEdit] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newGodownName || !newGodownLocation) { showModal("Please enter both Godown Name and Location.", "error"); return; }
    if (godowns.some(g => g.name.toLowerCase() === newGodownName.toLowerCase())) { showModal("A godown with this name already exists.", "error"); return; }
    await addGodown({ name: newGodownName, location: newGodownLocation, isSystemGodown: false }); // Mark new godowns as not system-managed
    setNewGodownName(''); setNewGodownLocation('');
  };

  const handleEditClick = (godown) => { setGodownToEdit(godown); setEditModalOpen(true); };

  return (
    <div className="container mx-auto p-6 bg-gradient-to-br from-red-50 to-orange-100 min-h-screen">
      <h1 className="text-4xl font-extrabold mb-8 text-red-800 text-center drop-shadow-md">Godown Management</h1>
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto mb-12 border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Add New Godown</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input label="Godown Name" name="newGodownName" value={newGodownName} onChange={(e) => setNewGodownName(e.target.value)} placeholder="e.g., Main Warehouse" required />
          <Input label="Location" name="newGodownLocation" value={newGodownLocation} onChange={(e) => setNewGodownLocation(e.target.value)} placeholder="e.g., Industrial Area, City" required />
          <div className="flex justify-center">
            <button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Add Godown</button>
          </div>
        </form>
      </div>

      <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Existing Godowns ({godowns.length})</h2>
        {godowns.length === 0 ? (<p className="text-gray-600 text-center">No godowns added yet.</p>) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Name</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Location</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Type</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {godowns.map(godown => (
                  <tr key={godown.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-800 text-sm">{godown.name}</td>
                    <td className="py-3 px-4 text-gray-800 text-sm">{godown.location}</td>
                    <td className="py-3 px-4 text-gray-800 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            godown.isSystemGodown ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                            {godown.isSystemGodown ? 'System' : 'Standard'}
                        </span>
                    </td>
                    <td className="py-3 px-4 text-gray-800 text-sm flex space-x-2">
                        {godown.id !== SALES_GODOWN_ID && ( // Only allow editing/deleting non-system godowns
                            <>
                                <button onClick={() => handleEditClick(godown)} className="text-blue-600 hover:text-blue-800 transition-colors duration-200" title="Edit Godown"><EditIcon /></button>
                                <button onClick={() => deleteGodown(godown.id)} className="text-red-600 hover:text-red-800 transition-colors duration-200" title="Delete Godown"><DeleteIcon /></button>
                            </>
                        )}
                        {godown.id === SALES_GODOWN_ID && (
                            <span className="text-gray-400 text-sm italic">System Godown</span>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editModalOpen && (<EditGodownModal godown={godownToEdit} onClose={() => setEditModalOpen(false)} />)}
    </div>
  );
};

const EditGodownModal = ({ godown, onClose }) => {
    const { updateGodown, showModal } = useContext(CoilContext);
    const [name, setName] = useState(godown.name);
    const [location, setLocation] = useState(godown.location);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !location) { showModal("Please enter both Godown Name and Location.", "error"); return; }
        await updateGodown(godown.id, { name, location });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[100]">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-sm w-full border border-gray-200">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Edit Godown</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input label="Name" name="name" value={name} onChange={(e) => setName(e.target.value)} type="text" required disabled={godown.isSystemGodown} />
                    <Input label="Location" name="location" value={location} onChange={(e) => setLocation(e.target.value)} type="text" required />
                    <div className="flex justify-end space-x-3 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors shadow">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const PrimaryProcessingReportView = () => {
    const { processingReports, deleteProcessingReport } = useContext(CoilContext);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('date-desc');
    const [expandedCoilId, setExpandedCoilId] = useState(null);

    const filteredAndSortedReports = useMemo(() => {
        return [...processingReports]
            .filter(report => {
                const reportDate = new Date(report.timestamp);
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;
                return (!start || reportDate >= start) && (!end || reportDate <= end);
            })
            .sort((a, b) => {
                if (sortBy === 'date-desc') return new Date(b.timestamp) - new Date(a.timestamp);
                if (sortBy === 'date-asc') return new Date(a.timestamp) - new Date(b.timestamp);
                if (sortBy === 'variance-desc') return b.weightVariance - a.weightVariance;
                if (sortBy === 'variance-asc') return a.weightVariance - b.weightVariance;
                return 0;
            });
    }, [processingReports, startDate, endDate, sortBy]);

    const totalExcessWeight = filteredAndSortedReports.filter(report => report.weightVariance > 0).reduce((sum, report) => sum + report.weightVariance, 0);
    const totalShortageWeight = filteredAndSortedReports.filter(report => report.weightVariance < 0).reduce((sum, report) => sum + report.weightVariance, 0);

    const toggleExpand = (coilId) => setExpandedCoilId(expandedCoilId === coilId ? null : coilId);

    return (
        <div>
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Filters & Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Input label="Start Date" value={startDate} onChange={(e) => setStartDate(e.target.value)} type="date" />
                    <Input label="End Date" value={endDate} onChange={(e) => setEndDate(e.target.value)} type="date" />
                    <Select label="Sort By" value={sortBy} onChange={(e) => setSortBy(e.target.value)} options={[
                        { value: 'date-desc', label: 'Date (Newest First)' }, { value: 'date-asc', label: 'Date (Oldest First)' },
                        { value: 'variance-desc', label: 'Variance (Highest First)' }, { value: 'variance-asc', label: 'Variance (Lowest First)' }
                    ]} />
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><h3 className="text-lg font-semibold text-gray-700 mb-1">Total Excess Weight:</h3><p className="text-green-600 text-2xl font-bold">{totalExcessWeight.toFixed(2)} kg</p></div>
                    <div><h3 className="text-lg font-semibold text-gray-700 mb-1">Total Shortage Weight:</h3><p className="text-red-600 text-2xl font-bold">{totalShortageWeight.toFixed(2)} kg</p></div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold text-gray-800">Processing Reports</h2></div>
                {filteredAndSortedReports.length === 0 ? (<p className="text-gray-600 text-center">No processing reports available yet or matching filters. End processing for a coil to generate one.</p>) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg overflow-hidden">
                            <thead className="bg-gray-100 border-b border-gray-200">
                                <tr>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Report ID</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Coil ID</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Initial Weight (kg)</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Processed Weight (kg)</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Final Coil Weight (kg)</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Variance (kg)</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Status</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Date</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedReports.map(report => (
                                    <React.Fragment key={report.id}>
                                        <tr className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="py-3 px-4 text-gray-800 text-sm">{report.id}</td>
                                            <td className="py-3 px-4 text-blue-600 text-sm font-medium cursor-pointer hover:underline" onClick={() => toggleExpand(report.rawCoilId)}>{report.coilId}</td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">{report.initialCoilWeight.toFixed(2)}</td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">{report.totalProcessedWeight.toFixed(2)}</td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">{report.finalCoilWeight.toFixed(2)}</td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">
                                                <span className={`${report.weightVariance > 0.01 ? 'text-green-600' : report.weightVariance < -0.01 ? 'text-red-600' : 'text-gray-600'} font-semibold`}>{report.weightVariance.toFixed(2)}</span>
                                            </td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${report.varianceStatus === 'Excess Weight Received' ? 'bg-green-100 text-green-800' : report.varianceStatus === 'Shortage of Weight' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {report.varianceStatus}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">{report.timestamp ? new Date(report.timestamp).toLocaleDateString() : 'N/A'}</td>
                                            <td className="py-3 px-4 text-gray-800 text-sm">
                                                <button onClick={() => deleteProcessingReport(report.id)} className="text-red-600 hover:text-red-800 transition-colors duration-200" title="Delete Report"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                        {expandedCoilId === report.rawCoilId && (
                                            <tr><td colSpan="9" className="py-2 px-4 bg-gray-100"><ProcessedItemsDisplay parentCoilId={report.rawCoilId} /></td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const ProcessedOutCoilsView = () => {
    const { rawCoils, processingReports } = useContext(CoilContext);

    const coilsWithProcessedOutDate = useMemo(() => {
        return rawCoils.filter(coil => coil.status === 'processed-out').map(coil => {
            const report = processingReports.find(r => r.rawCoilId === coil.id);
            return { ...coil, processedOutDate: report ? new Date(report.timestamp).toLocaleDateString() : 'N/A' };
        }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [rawCoils, processingReports]);

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Processed Out Coils ({coilsWithProcessedOutDate.length})</h2>
            {coilsWithProcessedOutDate.length === 0 ? (<p className="text-gray-600 text-center">No coils have been marked as 'processed-out' yet.</p>) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Coil ID</th>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Initial Weight (kg)</th>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Godown</th>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Date Received</th>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Date Processed Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coilsWithProcessedOutDate.map(coil => (
                                <tr key={coil.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="py-3 px-4 text-gray-800 text-sm">{coil.coilId}</td>
                                    <td className="py-3 px-4 text-gray-800 text-sm">{coil.initialWeight.toFixed(2)}</td>
                                    <td className="py-3 px-4 text-gray-800 text-sm">{coil.godown}</td>
                                    <td className="py-3 px-4 text-gray-800 text-sm">{new Date(coil.timestamp).toLocaleDateString()}</td>
                                    <td className="py-3 px-4 text-gray-800 text-sm">{coil.processedOutDate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


const ProcessingReports = () => {
    const [reportType, setReportType] = useState('processing');
    return (
        <div className="container mx-auto p-6 bg-gradient-to-br from-orange-50 to-amber-100 min-h-screen">
            <h1 className="text-4xl font-extrabold mb-8 text-orange-800 text-center drop-shadow-md">Reports</h1>
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Report Type</h2>
                <div className="flex justify-center">
                    <select value={reportType} onChange={(e) => setReportType(e.target.value)} className="shadow-sm appearance-none border rounded-md py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 max-w-sm w-full">
                        <option value="processing">Coil Processing Reports (Variance)</option>
                        <option value="processed-out">Processed Out Raw Coils</option>
                    </select>
                </div>
            </div>
            {reportType === 'processing' && <PrimaryProcessingReportView />}
            {reportType === 'processed-out' && <ProcessedOutCoilsView />}
        </div>
    );
};

const StockTransfer = () => {
    const { rawCoils, processedItems, godowns, transferStock, showModal, SALES_GODOWN_NAME } = useContext(CoilContext);
    const [itemType, setItemType] = useState('');
    const [selectedItemId, setSelectedItemId] = useState('');
    const [fromGodown, setFromGodown] = useState('');
    const [toGodown, setToGodown] = useState('');
    const [transferSheetQuantity, setTransferSheetQuantity] = useState('');
    const [transferSheetWeight, setTransferSheetWeight] = useState('');
    const [transferSlitCoilWeight, setTransferSlitCoilWeight] = useState('');

    const allTransferableItems = useMemo(() => {
        let items = [];
        if (itemType === 'rawCoil') items = rawCoils.filter(coil => coil.status !== 'processed-out');
        else if (itemType === 'sheet') items = processedItems.filter(item => item.type === 'sheet');
        else if (itemType === 'slitCoil') items = processedItems.filter(item => item.type === 'slit-coil');
        return fromGodown ? items.filter(item => item.godown === fromGodown) : items;
    }, [itemType, fromGodown, rawCoils, processedItems]);

    const displayItemKey = useCallback((item) => {
      if (item.type === 'rawCoil' || !item.type) return `${item.coilId} (${item.thickness}mm x ${item.width}mm, ${item.weight?.toFixed(2)}kg, Godown: ${item.godown})`;
      if (item.type === 'sheet') return `${item.coilId} - Sheet ${item.quantity}pcs (${item.weight?.toFixed(2)}kg, Godown: ${item.godown})`;
      if (item.type === 'slit-coil') return `${item.coilId} - Slit ${item.slitWidth}mm (${item.weight?.toFixed(2)}kg, Godown: ${item.godown})`;
      return item.id;
    }, []);

    const filterItemKeys = useMemo(() => {
        if (itemType === 'rawCoil') return ['coilId', 'thickness', 'width', 'weight', 'godown'];
        if (itemType === 'sheet') return ['coilId', 'length', 'quantity', 'weight', 'godown', 'thickness', 'width', 'actualProcessedThickness'];
        if (itemType === 'slitCoil') return ['coilId', 'slitWidth', 'weight', 'godown', 'thickness'];
        return ['id'];
    }, [itemType]);

    useEffect(() => { setSelectedItemId(''); setTransferSheetQuantity(''); setTransferSheetWeight(''); setTransferSlitCoilWeight(''); }, [itemType]);
    useEffect(() => {
        if (selectedItemId && (itemType === 'sheet' || itemType === 'slitCoil')) {
            const selectedItem = allTransferableItems.find(item => item.id === selectedItemId);
            if (selectedItem) {
                if (itemType === 'sheet') { setTransferSheetQuantity(selectedItem.quantity); setTransferSheetWeight(selectedItem.weight?.toFixed(2)); }
                else { setTransferSlitCoilWeight(selectedItem.weight?.toFixed(2)); }
            }
        }
    }, [selectedItemId, itemType, allTransferableItems]);

    const handleTransfer = async (e) => {
        e.preventDefault();
        if (!itemType || !fromGodown || !toGodown) { showModal("Please select item type, 'From' godown, and 'To' godown.", "error"); return; }
        if (fromGodown === toGodown) { showModal("Cannot transfer to the same godown.", "error"); return; }
        if (itemType === 'scrap') { showModal("Scrap transfer is not supported.", "error"); return; }
        if (fromGodown === SALES_GODOWN_NAME) { showModal("Cannot transfer items FROM the 'Sales' godown using this interface. Sales items are considered 'sold'.", "error"); return; }


        let success = false;
        if (itemType === 'sheet') {
            const quantity = parseInt(transferSheetQuantity); const weight = parseFloat(transferSheetWeight);
            if (!selectedItemId || isNaN(quantity) || quantity <= 0 || isNaN(weight) || weight <= 0) { showModal("Please select an item and enter valid positive quantity and weight for sheets.", "error"); return; }
            const selectedSheet = allTransferableItems.find(item => item.id === selectedItemId);
            if (selectedSheet.quantity < quantity || selectedSheet.weight < weight) { showModal("Transfer quantity or weight exceeds available stock for this sheet item.", "error"); return; }
            success = await transferStock(itemType, selectedItemId, fromGodown, toGodown, quantity, weight);
        } else if (itemType === 'slitCoil') {
            const weight = parseFloat(transferSlitCoilWeight);
            if (!selectedItemId || isNaN(weight) || weight <= 0) { showModal("Please select an item and enter a valid positive weight for slit coil.", "error"); return; }
            const selectedSlitCoil = allTransferableItems.find(item => item.id === selectedItemId);
            if (selectedSlitCoil.weight < weight) { showModal("Transfer weight exceeds available stock for this slit coil item.", "error"); return; }
            success = await transferStock(itemType, selectedItemId, fromGodown, toGodown, null, weight);
        } else { // Raw Coil
            if (!selectedItemId) { showModal("Please select a raw coil to transfer.", "error"); return; }
            success = await transferStock(itemType, selectedItemId, fromGodown, toGodown);
        }
        if (success) { setFromGodown(''); setToGodown(''); setItemType(''); }
    };

    return (
        <div className="container mx-auto p-6 bg-gradient-to-br from-sky-50 to-blue-100 min-h-screen">
            <h1 className="text-4xl font-extrabold mb-8 text-sky-800 text-center drop-shadow-md">Stock Transfer</h1>
            <form onSubmit={handleTransfer} className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto border border-gray-200">
                <Select label="Item Type to Transfer" value={itemType} onChange={(e) => setItemType(e.target.value)} options={[{ value: '', label: 'Select Type' }, { value: 'rawCoil', label: 'Raw Coil' }, { value: 'sheet', label: 'Sheet' }, { value: 'slitCoil', label: 'Slit Coil' }]} required />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <Select label="From Godown" value={fromGodown} onChange={(e) => setFromGodown(e.target.value)} options={[{ value: '', label: 'Select Godown' }, ...godowns.filter(g => g.name !== SALES_GODOWN_NAME).map(g => ({ value: g.name, label: g.name }))]} required />
                    <Select label="To Godown" value={toGodown} onChange={(e) => setToGodown(e.target.value)} options={[{ value: '', label: 'Select Godown' }, ...godowns.map(g => ({ value: g.name, label: g.name }))]} required />
                </div>
                {(itemType === 'rawCoil' || itemType === 'sheet' || itemType === 'slitCoil') && (
                    <div className="mb-6">
                        <SearchableItemInput label="Select Item" options={allTransferableItems} value={selectedItemId} onChange={setSelectedItemId}
                            displayKey={displayItemKey} filterKeys={filterItemKeys} placeholder={`Search by ${itemType === 'rawCoil' ? 'Coil ID, Thickness, Weight' : itemType === 'sheet' ? 'Coil ID, Quantity, Weight' : 'Coil ID, Slit Width, Weight'}`} required={true}
                        />
                        {allTransferableItems.length === 0 && fromGodown && (
                            <p className="text-red-500 text-xs italic mt-2">No {itemType}s available in "{fromGodown}".</p>
                        )}
                    </div>
                )}
                {itemType === 'sheet' && selectedItemId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <Input label="Number of Sheets to Transfer" value={transferSheetQuantity} onChange={(e) => setTransferSheetQuantity(e.target.value)} type="number" step="1" min="1" placeholder="Enter quantity" required />
                        <Input label="Total Weight to Transfer (kg)" value={transferSheetWeight} onChange={(e) => setTransferSheetWeight(e.target.value)} type="number" step="0.01" placeholder="Enter weight" required />
                    </div>
                )}
                {itemType === 'slitCoil' && selectedItemId && (
                    <div className="mb-6">
                        <Input label="Weight to Transfer (kg)" value={transferSlitCoilWeight} onChange={(e) => setTransferSlitCoilWeight(e.target.value)} type="number" step="0.01" placeholder="Enter weight" required />
                    </div>
                )}
                <div className="flex justify-center">
                    <button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Transfer Stock</button>
                </div>
            </form>
        </div>
    );
};

const CoilDetailsForMonthlyReport = ({ coil }) => {
    const { processedItems } = useContext(CoilContext);
    const items = processedItems.filter(item => item.parentId === coil.id);
    return (
        <div className="bg-gray-50 p-4 rounded-md mt-2 ml-4 border border-gray-200">
            <h4 className="text-md font-semibold mb-2 text-gray-700">Processed Outputs for {coil.coilId}:</h4>
            <p className="text-sm text-gray-700 mb-2">Current Balance: {coil.weight?.toFixed(2) || '0.00'} kg | Godown: {coil.godown || 'N/A'}</p>
            {items.length === 0 ? (<p className="text-gray-500 text-sm italic">No processed items found for this coil.</p>) : (
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                    {items.map(item => (
                        <li key={item.id}>
                            <strong>{item.type === 'sheet' ? 'Sheet' : 'Slit Coil'}</strong>: {' '}
                            {item.type === 'sheet' ? `${item.actualProcessedThickness?.toFixed(2) || 'N/A'}x${item.width?.toFixed(2) || 'N/A'}x${item.length?.toFixed(2) || 'N/A'} (${item.quantity || 1} Pcs)` : `${item.slitWidth?.toFixed(2) || 'N/A'}mm Slit W`}, {item.weight?.toFixed(2) || 'N/A'}kg (Godown: {item.godown}) - {new Date(item.timestamp).toLocaleDateString()}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


const MonthlyCoilReport = () => {
    const { rawCoils, deleteRawCoil, SALES_GODOWN_NAME } = useContext(CoilContext);
    const [expandedCoilId, setExpandedCoilId] = useState(null);

    const coilsInStandardGodowns = rawCoils.filter(coil => coil.godown !== SALES_GODOWN_NAME);

    const coilsByMonth = useMemo(() => {
        return coilsInStandardGodowns.reduce((acc, coil) => {
            const month = coil.receivedMonth;
            if (!acc[month]) acc[month] = { totalWeight: 0, coils: [] };
            acc[month].totalWeight += coil.initialWeight;
            acc[month].coils.push(coil);
            return acc;
        }, {});
    }, [coilsInStandardGodowns]);

    const sortedMonths = Object.keys(coilsByMonth).sort().reverse();

    const toggleExpand = (coilId) => setExpandedCoilId(expandedCoilId === coilId ? null : coilId);

    const getCoilRowClass = (coil) => {
        const initialWeight = parseFloat(coil.initialWeight);
        const currentWeight = parseFloat(coil.weight);
        const isProcessedOut = coil.status === 'processed-out';
        const isUntouched = Math.abs(currentWeight - initialWeight) < 0.01 && coil.status === 'available';
        const isPartiallyProcessed = Math.abs(currentWeight - initialWeight) >= 0.01 && coil.status === 'partially-processed';

        if (isProcessedOut) return 'bg-red-50 hover:bg-red-100';
        if (isUntouched) return 'bg-green-50 hover:bg-green-100';
        if (isPartiallyProcessed) return 'bg-yellow-50 hover:bg-yellow-100';
        return 'bg-white hover:bg-gray-50';
    };

    return (
        <div className="container mx-auto p-6 bg-gradient-to-br from-gray-50 to-neutral-100 min-h-screen">
            <h1 className="text-4xl font-extrabold mb-8 text-gray-800 text-center drop-shadow-md">Monthly Raw Coil Report (Excluding Sales Godown)</h1>

            {sortedMonths.length === 0 ? (
                <p className="text-gray-600 text-center">No raw coils recorded yet with monthly data in standard godowns.</p>
            ) : (
                <div className="space-y-8">
                    {sortedMonths.map(month => (
                        <div key={month} className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold text-gray-800">{new Date(`${month}-01`).toLocaleString('default', { year: 'numeric', month: 'long' })}</h2></div>
                            <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-700 mb-1">Total Weight Received:</h3>
                                <p className="text-blue-600 text-2xl font-bold">{coilsByMonth[month].totalWeight.toFixed(2)} kg</p>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-3">Coils Received:</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white rounded-lg overflow-hidden">
                                    <thead className="bg-gray-100 border-b border-gray-200">
                                        <tr>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Coil ID</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Thickness (mm)</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Width (mm)</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Initial Weight (kg)</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Current Weight (kg)</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Godown</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Status</th>
                                            <th className="py-2 px-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {coilsByMonth[month].coils.map(coil => (
                                            <React.Fragment key={coil.id}>
                                                <tr className={`border-b border-gray-200 ${getCoilRowClass(coil)}`}>
                                                    <td className="py-2 px-3 text-blue-600 text-sm font-medium cursor-pointer hover:underline" onClick={() => toggleExpand(coil.id)}>{coil.coilId}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">{coil.thickness.toFixed(2)}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">{coil.width.toFixed(2)}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">{coil.initialWeight.toFixed(2)}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">{coil.weight.toFixed(2)}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">{coil.godown}</td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${coil.status === 'available' ? 'bg-green-100 text-green-800' : coil.status === 'partially-processed' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                                                          {coil.status.replace('-', ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-800 text-sm">
                                                        <button onClick={() => deleteRawCoil(coil.id)} className="text-red-600 hover:text-red-800 transition-colors duration-200" title="Delete Coil"><DeleteIcon /></button>
                                                    </td>
                                                </tr>
                                                {expandedCoilId === coil.id && (
                                                    <tr><td colSpan="8" className="py-2 px-4 bg-gray-100"><CoilDetailsForMonthlyReport coil={coil} /></td></tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const Settings = () => {
    const { appSettings, updateAppSettings, showModal, appDataUserId, handleAdminLogout } = useContext(CoilContext);
    const [logoUrl, setLogoUrl] = useState(appSettings.logoUrl || '');

    useEffect(() => { setLogoUrl(appSettings.logoUrl || ''); }, [appSettings]);

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        if (!appDataUserId) { showModal("You must be logged in as admin to save settings.", "error"); return; }
        await updateAppSettings({ logoUrl });
    };

    return (
        <div className="container mx-auto p-6 bg-gradient-to-br from-blue-50 to-gray-100 min-h-screen">
            <h1 className="text-4xl font-extrabold mb-8 text-blue-800 text-center drop-shadow-md">System Settings</h1>
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto border border-gray-200">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Admin Session</h2>
                {appDataUserId ? (
                    <div className="text-center">
                        <p className="text-green-600 font-semibold mb-4">You are logged in as Admin ({appDataUserId}).</p>
                        <button onClick={handleAdminLogout} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 shadow">Logout Admin Session</button>
                    </div>
                ) : (<p className="text-gray-600 text-center">You are not logged in as Admin. Log in from the main page.</p>)}
            </div>

            {appDataUserId && (
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto mt-8 border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800">Branding Settings</h2>
                    <form onSubmit={handleSaveSettings} className="space-y-4">
                        <div>
                            <label htmlFor="logoUrl" className="block text-gray-700 text-sm font-bold mb-2">Logo URL:</label>
                            <input type="url" id="logoUrl" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} className="shadow-sm border rounded-md w-full py-2 px-3 text-gray-700" placeholder="https://example.com/your-logo.png" />
                            <p className="text-gray-500 text-xs italic mt-1">This logo will be displayed in the application header.</p>
                        </div>
                        <div className="flex justify-center">
                            <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 shadow">Save Branding Settings</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};


const EditItemModal = ({ item, onClose, godowns }) => {
    const { updateRawCoil, updateProcessedItem, showModal } = useContext(CoilContext);
    const [formData, setFormData] = useState({});

    useEffect(() => { if (item && item.data) setFormData({ ...item.data }); }, [item]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (item.type === 'rawCoil') {
                await updateRawCoil(item.data.id, {
                    coilId: formData.coilId, thickness: parseFloat(formData.thickness), width: parseFloat(formData.width),
                    weight: parseFloat(formData.weight), godown: formData.godown,
                });
            } else if (item.type === 'processedItem') {
                if (item.data.type === 'sheet') {
                    await updateProcessedItem(item.data.id, {
                        length: parseFloat(formData.length), quantity: parseInt(formData.quantity),
                        weight: parseFloat(formData.weight), godown: formData.godown, actualProcessedThickness: parseFloat(formData.actualProcessedThickness),
                    });
                } else if (item.data.type === 'slit-coil') {
                    await updateProcessedItem(item.data.id, {
                        slitWidth: parseFloat(formData.slitWidth), weight: parseFloat(formData.weight), godown: formData.godown,
                    });
                }
            }
            onClose();
        } catch (error) { showModal(`Failed to update item: ${error.message}`, "error"); }
    };

    if (!item) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[100]">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-lg w-full border border-gray-200">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Edit {item.type === 'rawCoil' ? 'Raw Coil' : 'Processed Item'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {item.type === 'rawCoil' && (
                        <>
                            <Input label="Coil ID" name="coilId" value={formData.coilId} onChange={handleChange} type="text" />
                            <Input label="Thickness (mm)" name="thickness" value={formData.thickness} onChange={handleChange} type="number" step="0.01" />
                            <Input label="Width (mm)" name="width" value={formData.width} onChange={handleChange} type="number" step="0.01" />
                            <Input label="Weight (kg)" name="weight" value={formData.weight} onChange={handleChange} type="number" step="0.01" />
                            <div>
                                <label htmlFor="godown" className="block text-gray-700 text-sm font-bold mb-1">Godown:</label>
                                <select id="godown" name="godown" value={formData.godown} onChange={handleChange} className="shadow-sm border rounded-md w-full py-2 px-3 text-gray-700">
                                    {godowns.map(g => (<option key={g.id} value={g.name}>{g.name}</option>))}
                                </select>
                            </div>
                        </>
                    )}
                    {item.type === 'processedItem' && item.data.type === 'sheet' && (
                        <>
                            <Input label="Sheet Length (mm)" name="length" value={formData.length} onChange={handleChange} type="number" step="0.01" />
                            <Input label="Quantity" name="quantity" value={formData.quantity} onChange={handleChange} type="number" step="1" />
                            <Input label="Actual Processed Thickness (mm)" name="actualProcessedThickness" value={formData.actualProcessedThickness} onChange={handleChange} type="number" step="0.01" />
                            <Input label="Weight (kg)" name="weight" value={formData.weight} onChange={handleChange} type="number" step="0.01" />
                            <div>
                                <label htmlFor="godown" className="block text-gray-700 text-sm font-bold mb-1">Godown:</label>
                                <select id="godown" name="godown" value={formData.godown} onChange={handleChange} className="shadow-sm border rounded-md w-full py-2 px-3 text-gray-700">
                                    {godowns.map(g => (<option key={g.id} value={g.name}>{g.name}</option>))}
                                </select>
                            </div>
                        </>
                    )}
                    {item.type === 'processedItem' && item.data.type === 'slit-coil' && (
                        <>
                            <Input label="Slit Width (mm)" name="slitWidth" value={formData.slitWidth} onChange={handleChange} type="number" step="0.01" />
                            <Input label="Weight (kg)" name="weight" value={formData.weight} onChange={handleChange} type="number" step="0.01" />
                            <div>
                                <label htmlFor="godown" className="block text-gray-700 text-sm font-bold mb-1">Godown:</label>
                                <select id="godown" name="godown" value={formData.godown} onChange={handleChange} className="shadow-sm border rounded-md w-full py-2 px-3 text-gray-700">
                                    {godowns.map(g => (<option key={g.id} value={g.name}>{g.name}</option>))}
                                </select>
                            </div>
                        </>
                    )}
                    <div className="flex justify-end space-x-3 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors shadow">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- New Coil Details Page ---
const CoilDetailsPage = ({ setCurrentPage }) => {
    const { rawCoils, processedItems, processingReports, showModal, revertCoilStatus, endCoilProcessing } = useContext(CoilContext); // Added endCoilProcessing
    const [selectedCoilId, setSelectedCoilId] = useState(null);

    const selectedCoil = rawCoils.find(coil => coil.id === selectedCoilId);
    const relatedProcessedItems = selectedCoil ? processedItems.filter(item => item.parentId === selectedCoil.id) : [];
    const relatedProcessingReport = selectedCoil ? processingReports.find(report => report.rawCoilId === selectedCoil.id) : null;

    const handleProcessCoilClick = () => { if (selectedCoil) setCurrentPage('processing', { preSelectedCoilId: selectedCoil.id }); else showModal("Please select a coil to process.", "info"); };

    const handleEndProcessingClick = () => {
        if (selectedCoil) {
            const currentCoil = rawCoils.find(c => c.id === selectedCoil.id);
            if (!currentCoil) { showModal("Selected coil not found.", "error"); return; }
            if (currentCoil.status === 'processed-out') { showModal("This coil is already marked as fully processed.", "info"); return; }
            const message = `End Processing for Coil: ${currentCoil.coilId}\n\nRemaining coil weight: ${currentCoil.weight?.toFixed(2) || '0.00'} kg.\nThis remaining weight will be accounted for in the variance calculation as a shortage if not fully processed.\n\nWarning: This action will mark the coil as 'processed-out' and cannot be undone.`;
            showModal(message, 'confirm', async () => { // Changed to async to await endCoilProcessing
                const success = await endCoilProcessing(selectedCoil.id); // Call endCoilProcessing directly
                if (success) {
                    setCurrentPage('coil-details', { preSelectedCoilId: selectedCoil.id }); // Stay on details, data will refresh
                }
            });
        } else { showModal("Please select a coil to end processing.", "info"); }
    };

    const handleRevertCoilClick = () => {
        if (selectedCoil) {
            showModal(`Are you sure you want to revert coil ${selectedCoil.coilId} to 'available' status? This will delete all associated processed items and reports.`, 'confirm', () => revertCoilStatus(selectedCoil.id));
        } else { showModal("Please select a coil to revert.", "info"); }
    };

    return (
        <div className="container mx-auto p-6 bg-gradient-to-br from-yellow-50 to-amber-100 min-h-screen">
            <h1 className="text-4xl font-extrabold mb-8 text-yellow-800 text-center drop-shadow-md">Coil Details & History</h1>
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto border border-gray-200 mb-8">
                <SearchableItemInput label="Select Raw Coil to View Details" options={rawCoils} value={selectedCoilId} onChange={setSelectedCoilId}
                    displayKey={(coil) => `${coil.coilId} (${coil.thickness}mm x ${coil.width}mm, ${coil.weight?.toFixed(2)}kg, Godown: ${coil.godown})`}
                    filterKeys={['coilId', 'thickness', 'width', 'weight', 'godown']} placeholder="Search by Coil ID, Thickness, Weight, Godown..." required={true}
                />
                 {rawCoils.length === 0 && (<p className="text-red-500 text-xs italic mt-2">No raw coils available. Add coils first.</p>)}
            </div>

            {selectedCoil ? (
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto border border-gray-200">
                    <h2 className="text-3xl font-bold mb-6 text-gray-800">Coil: {selectedCoil.coilId}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-gray-700">
                        <p><strong>Initial Weight:</strong> {selectedCoil.initialWeight?.toFixed(2)} kg</p>
                        <p><strong>Current Weight:</strong> {selectedCoil.weight?.toFixed(2)} kg</p>
                        <p><strong>Thickness:</strong> {selectedCoil.thickness?.toFixed(2)} mm</p>
                        <p><strong>Width:</strong> {selectedCoil.width?.toFixed(2)} mm</p>
                        <p><strong>Godown:</strong> {selectedCoil.godown}</p>
                        <p><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-sm font-semibold ${selectedCoil.status === 'available' ? 'bg-green-100 text-green-800' : selectedCoil.status === 'partially-processed' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>{selectedCoil.status.replace('-', ' ')}</span></p>
                        <p><strong>Received Month:</strong> {selectedCoil.receivedMonth}</p>
                        <p><strong>Entry Date:</strong> {new Date(selectedCoil.timestamp).toLocaleDateString()}</p>
                    </div>

                    <div className="flex justify-start space-x-4 mt-6 mb-8 border-t pt-6 border-gray-200">
                        {selectedCoil.status !== 'processed-out' && (
                            <button onClick={handleProcessCoilClick} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Process This Coil</button>
                        )}
                        {selectedCoil.status !== 'processed-out' && (
                            <button onClick={handleEndProcessingClick} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">End Processing For This Coil</button>
                        )}
                        {selectedCoil.status === 'processed-out' && (
                            <button onClick={handleRevertCoilClick} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all duration-300 transform hover:scale-105 shadow-md">Revert to In Process</button>
                        )}
                    </div>

                    <div className="mt-8">
                        <h3 className="text-2xl font-bold mb-4 text-gray-800">Processing History:</h3>
                        {relatedProcessedItems.length === 0 ? (<p className="text-gray-600">No processed items found for this coil yet.</p>) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white rounded-lg overflow-hidden border border-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Item Type</th>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Dimensions</th>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Quantity</th>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Weight (kg)</th>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Godown</th>
                                            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Date Processed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {relatedProcessedItems.map(item => (
                                            <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                <td className="py-3 px-4 text-gray-800 text-sm">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.type === 'sheet' ? 'bg-indigo-100 text-indigo-800' : 'bg-pink-100 text-pink-800'}`}>
                                                        {item.type === 'sheet' ? 'Sheet' : 'Slit Coil'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-gray-800 text-sm">
                                                    {item.type === 'sheet' ? `${item.actualProcessedThickness?.toFixed(2) || 'N/A'}x${item.width?.toFixed(2) || 'N/A'}x${item.length?.toFixed(2) || 'N/A'}` : `${item.slitWidth?.toFixed(2) || 'N/A'}mm Slit W`}
                                                </td>
                                                <td className="py-3 px-4 text-gray-800 text-sm">{item.quantity || 'N/A'}</td>
                                                <td className="py-3 px-4 text-gray-800 text-sm">{item.weight?.toFixed(2)}</td>
                                                <td className="py-3 px-4 text-gray-800 text-sm">{item.godown}</td>
                                                <td className="py-3 px-4 text-gray-800 text-sm">{new Date(item.timestamp).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="mt-8 border-t pt-6 border-gray-200">
                        <h3 className="text-2xl font-bold mb-4 text-gray-800">Processing Report (Variance):</h3>
                        {relatedProcessingReport ? (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <p><strong>Report ID:</strong> {relatedProcessingReport.id}</p>
                                <p><strong>Total Processed Output Weight:</strong> {relatedProcessingReport.totalProcessedWeight?.toFixed(2)} kg</p>
                                <p><strong>Final Remaining Coil Weight:</strong> {relatedProcessingReport.finalCoilWeight?.toFixed(2)} kg</p>
                                <p>
                                    <strong>Weight Variance:</strong>{' '}
                                    <span className={`${relatedProcessingReport.weightVariance > 0.01 ? 'text-green-600' : relatedProcessingReport.weightVariance < -0.01 ? 'text-red-600' : 'text-gray-600'} font-semibold`}>
                                        {relatedProcessingReport.weightVariance?.toFixed(2)} kg
                                    </span>
                                </p>
                                <p>
                                    <strong>Status:</strong>{' '}
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${relatedProcessingReport.varianceStatus === 'Excess Weight Received' ? 'bg-green-100 text-green-800' : relatedProcessingReport.varianceStatus === 'Shortage of Weight' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {relatedProcessingReport.varianceStatus}
                                    </span>
                                </p>
                                <p><strong>Report Date:</strong> {new Date(relatedProcessingReport.timestamp).toLocaleDateString()}</p>
                            </div>
                        ) : (<p className="text-gray-600">No final processing report found for this coil yet. A report is generated when processing for the coil is "ended".</p>)}
                    </div>
                </div>
            ) : (<p className="text-center text-gray-600 text-lg">Select a coil from the search bar above to view its details.</p>)}
        </div>
    );
};

// --- Main App Content Component (previously named App) ---
const MainAppContent = () => {
  const [currentPage, setCurrentPageInternal] = useState('dashboard');
  const [pageParams, setPageParams] = useState({});

  const { appSettings, handleAdminLogout } = useContext(CoilContext);

  const setCurrentPage = useCallback((page, params = {}) => { setCurrentPageInternal(page); setPageParams(params); }, []);

  console.log('MainAppContent rendered. Current page:', currentPage, 'with params:', pageParams);

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <header className="bg-indigo-700 text-white shadow-lg sticky top-0 z-50">
        <nav className="container mx-auto p-4 flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-shrink-0 text-white mr-6">
              {appSettings.logoUrl ? (<img src={appSettings.logoUrl} alt="Company Logo" className="h-8 mr-3 rounded-md" onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/32x32/FF0000/FFFFFF?text=Logo"; }}/>) : (<span className="font-extrabold text-2xl tracking-tight">CoilFlow</span>)}
          </div>
          <div className="flex-grow flex items-center justify-center">
            <div className="text-sm flex space-x-6">
              <NavItem icon={<HomeIcon />} label="Dashboard" onClick={() => setCurrentPage('dashboard')} active={currentPage === 'dashboard'} />
              <NavItem icon={<PlusIcon />} label="Add Coil" onClick={() => setCurrentPage('coil-input')} active={currentPage === 'coil-input'} />
              <NavItem icon={<HammerIcon />} label="Process" onClick={() => setCurrentPage('processing')} active={currentPage === 'processing'} />
              <NavItem icon={<PackageIcon />} label="Inventory" onClick={() => setCurrentPage('inventory')} active={currentPage === 'inventory'} />
              <NavItem icon={<WarehouseIcon />} label="Godowns" onClick={() => setCurrentPage('godown-management')} active={currentPage === 'godown-management'} />
              <NavItem icon={<FileTextIcon />} label="Reports" onClick={() => setCurrentPage('processing-reports')} active={currentPage === 'processing-reports'} />
              <NavItem icon={<BoxIcon />} label="Transfer" onClick={() => setCurrentPage('stock-transfer')} active={currentPage === 'stock-transfer'} />
              <NavItem icon={<CalendarDaysIcon />} label="Monthly Report" onClick={() => setCurrentPage('monthly-coil-report')} active={currentPage === 'monthly-coil-report'} />
              <NavItem icon={<SearchIcon />} label="Coil Details" onClick={() => setCurrentPage('coil-details')} active={currentPage === 'coil-details'} />
              <NavItem icon={<SettingsIcon />} label="Settings" onClick={() => setCurrentPage('settings')} active={currentPage === 'settings'} />
            </div>
          </div>
          <div className="ml-auto">
            <button onClick={handleAdminLogout} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors shadow">Logout</button>
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {currentPage === 'dashboard' && <Dashboard setCurrentPage={setCurrentPage} />}
        {currentPage === 'coil-input' && <CoilInput />}
        {currentPage === 'processing' && <Processing preSelectedCoilId={pageParams.preSelectedCoilId} setCurrentPage={setCurrentPage} />}
        {currentPage === 'inventory' && <Inventory />}
        {currentPage === 'godown-management' && <GodownManagement />}
        {currentPage === 'processing-reports' && <ProcessingReports />}
        {currentPage === 'stock-transfer' && <StockTransfer />}
        {currentPage === 'monthly-coil-report' && <MonthlyCoilReport />}
        {currentPage === 'coil-details' && <CoilDetailsPage setCurrentPage={setCurrentPage} />}
        {currentPage === 'settings' && <Settings />}
      </main>

      <Modal />
      <Notification />
    </div>
  );
};

const NavItem = ({ icon, label, onClick, active }) => (
  <button onClick={onClick} className={`flex flex-col items-center p-2 rounded-lg transition-all duration-300 ${active ? 'bg-indigo-800 text-white shadow-lg transform scale-105' : 'text-indigo-100 hover:bg-indigo-600 hover:text-white'} focus:outline-none focus:ring-2 focus:ring-indigo-300`}>
    <div className="w-6 h-6 mb-1">{icon}</div>
    <span className="text-xs font-medium">{label}</span>
  </button>
);

// --- Main App Entry Point ---
const App = () => {
  return (
    <>
      <script src="https://cdn.tailwindcss.com"></script>
      <script type="text/javascript">
        {`
          tailwind.config = {
            theme: {
              extend: {
                fontFamily: {
                  sans: ['Inter', 'sans-serif'],
                },
              },
            },
          };
        `}
      </script>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          body {
            font-family: 'Inter', sans-serif;
          }
        `}
      </style>
      <CoilProvider>
        <CoilContext.Consumer>
          {({ isAdminLoggedIn }) => (isAdminLoggedIn ? <MainAppContent /> : <LoginPage />)}
        </CoilContext.Consumer>
      </CoilProvider>
    </>
  );
};

export default App;
